import { nextTick } from 'vue';
import { v4 as uuid, validate as uuidValidate } from 'uuid';
import { defineStore } from 'pinia';
import {
  find, get, values, cloneDeep, isNumber, sortBy, findLastIndex,
  first, flatten, last, map, merge, omit, isEmpty, keyBy, throttle, isNil, truncate,
  partial, uniq, uniqBy, forEach, defer,
} from 'lodash-es';
import {
  docFromString,
  findDocNode,
  toDocString,
} from '@discoveryedu/titan-text-editor';
import { tokens as nebulaTokens } from '@discoveryedu/nebula-core/dist/data/nebula_tokens.json';
import { bus } from '@/lib/eventBus';
import
SharedDocument,
{
  yjsHelper,
  SharedDocumentPollingError,
  SharedDocumentAuthError,
  SharedDocumentCreateSessionError,
  SharedDocumentLoadSessionError,
  UndoManager,
  DocumentChangeStackItem,
  DocumentChange,
  LOCAL_TRANSACTION,
  LOCAL_TRANSACTION_NOT_UNDOABLE,
  LOCAL_TRANSACTION_UNDO_MANAGER,
  LOCAL_TRANSACTIONS,
} from '@/shared-document';
import {
  COMPLETED_ASSIGNMENT_STATUSES,
  NOT_STARTED_ASSIGNMENT_STATUS,
  IN_PROGRESS_ASSIGNMENT_STATUS_ID,
  USER_COLORS,
  EDITOR_CANVAS_HEIGHT,
  EVENT_TYPE_DOCUMENT,
  MODAL_CANVAS_WIDTH,
  MODAL_CANVAS_HEIGHT,
  APP_COPY_DATA_TYPE,
} from '@/lib/constants';
import i18next from '@/lib/i18next';
import { getLocalData } from '@/lib/localData';
import { decodeHTML, isGuid } from '@/lib/utils';
import {
  layoutToPixels,
} from '@/lib/utils/grid';
import chain from '@/lib/utils/chain';
import {
  getNextTitle,
  getWindowTitleFormat,
} from '@/lib/utils/titleFormat';
import { removeWindowLinks } from '@/lib/utils/module';
import {
  copyModals,
  copyModules,
  copyPages,
  getModalsForModule,
  getModalsForPages,
  getPastableModules,
  getPasteModuleFromAssetId,
  updateModulesOnPaste,
  setPastedModulesLayout,
  setPastedModulesOptions,
  setPastedModulesDefaultLayout,
  isPasteChangingLayout,
  validatePasteData,
} from '@/lib/utils/copyPaste';
import * as utils from '@/lib/utils/store';
import {
  useAppStore,
  useAssessmentStore,
  useAssetStore,
  useChatStore,
  useConfirmationStore,
  useErrorStore,
  useModalStore,
  useToastStore,
  useTrackingStore,
  useUploadStore,
  useUserStore,
} from '@/stores';
import { DraftIsDeletedError } from '@/lib/errors';
import moduleDefaultsList from '@/lib/constants/moduleDefaults';
import queue from '@/lib/queue';
import * as types from '@/lib/constants/store';

const nebulaSpace1x = parseInt(nebulaTokens.space['1x'], 10);

const throttlePatchDraftDescription = throttle((patch) => {
  queue.add(patch);
}, 1000);

const transactionIsLocal = (transaction) => LOCAL_TRANSACTIONS.includes(transaction.origin);

// Holds the SharedDocument instance for the draft currently being edited.
let sharedDoc = null;

// Undo manager for the current editing session.
let undoManager;
const applyUndoable = (stackItem) => {
  // Ensure changes are applied to the yDoc.
  if (stackItem.apply) {
    stackItem.apply();
  }
  // If there is an undo manager, add to undo stack.
  if (undoManager) {
    undoManager.add(stackItem);
  }
};

const useEditorStore = defineStore('studio-editor', {
  state: () => ({
    overlayThumbnail: null,
    addModuleDrawerIsOpen: false,
    activeCanvasName: null,
    currentModalCanvasId: null,
    currentPageId: null,
    draft: {},
    accessLevelForDraft: null, // don't access this directly; use the getter draftAccessLevel()
    editorMenuSettings: {},
    editorDrawers: {
      addModule: false,
      chat: false,
      customization: false,
      notes: false,
      notesFullscreen: true,
      highlightNotes: false,
    },
    editorDrawersAreAnimated: true,
    editorCarouselPageIndex: null,
    editorCarouselIsAnimated: true,
    editorIsFullscreen: false,
    editorViewModePreview: false,
    editorSnapGrid: null,
    editorStatusMessage: null,
    editorEvents: null,
    publishedCreationId: null,
    saving: false,
    teiErrors: {},
    thumbnailLoaders: [],
    trackThumbnailLoaders: false,
    topNavDropdownIsOpen: false,
    topNavDrawerIsOpen: false,
    urlRequestCache: {},
    isPreviewMode: false,
    studentView: null,
    canUndo: false,
    canRedo: false,
    moduleClipboard: null,
    videoDurations: {},
    // Fields used for Editor collaborative editing session.
    isConnected: false,
    connectedUsers: [],
    connectedUsersColors: {},
    session: {},
    assignmentId: null,
  }),
  getters: {
    isEditingRestricted() {
      // grab the concept page
      if (this.draft?.external_references) {
        const conceptPageReference = this.draft.external_references.find(
          (ref) => ref.reference_type === 'concept_page',
        );
        // restricted if the draft is a lesson, ucc, and the concept page reference exists
        return this.draftIsLesson && !this.draftIsDeCreated && conceptPageReference;
      }
      return false;
    },
    pagesSorted: (state) => {
      // no pages? no go
      if (!state.draft || !state.draft.pages) return null;

      const { pages } = state.draft;
      return sortBy(pages, ['sort_index', 'id']);
    },
    pagesById: (state) => keyBy(state.draft?.pages, 'id'),
    sectionsById: (state) => keyBy(state.draft?.options?.groups, 'id'),
    isMysteryTheme() {
      return get(this, 'currentPage.options.theme.name') === 'mystery-science';
    },
    groupedPages(state) {
      // Group pages together by section id. This is used by the PageList
      // and BoardNavigationOverlay, which render pages inside their sections
      const pages = this.pagesSorted || [];

      const sections = keyBy(state?.draft?.options?.groups || [], 'id');

      return pages.reduce((acc, currPage, index) => {
        // It's difficult to know what index a page is if we don't add it here,
        // since the pages won't be in a single-level array anymore
        const formattedCurrPage = {
          ...currPage,
          index,
        };

        const sectionId = formattedCurrPage.options && formattedCurrPage.options.group_id;
        const section = sections[sectionId];

        /*
          If the current page has no section id or if the section
          does not exist, it can be left alone.
        */
        if (!sectionId || !section) {
          acc.push(formattedCurrPage);
          return acc;
        }

        // If the current page is the first one or of a different
        // section than the previous page, it needs to be grouped
        // on its own
        if (!acc.length || last(acc).sectionId !== sectionId) {
          acc.push({
            isSection: true,
            sectionId,
            section,
            pages: [formattedCurrPage],
          });
          return acc;
        }

        // Otherwise the current page can be added to the same group
        // as the previous page
        last(acc).pages.push(formattedCurrPage);
        return acc;
      }, []);
    },
    closestFullscreenVisiblePage() {
      return () => {
        if (!get(this, 'currentPage.options.fullscreen_skip')) {
          return this.currentPage;
        }

        const pages = this.pagesSorted;
        const currentPageIdx = pages.findIndex((page) => (page.id === this.currentPageId));

        const nextPages = pages.slice(currentPageIdx + 1);
        const previousPages = pages.slice(0, currentPageIdx).reverse();
        const closestVisiblePage = chain(previousPages)
          .zip(nextPages)
          .flatten()
          .reject((page) => isNil(page))
          .find((page) => !get(page, 'options.fullscreen_skip'))
          .value();

        return closestVisiblePage;
      };
    },
    currentPageIndex(state) {
      const pages = this.pagesSorted;
      if (!pages) {
        return -1;
      }
      return pages.findIndex((page) => (page.id === state.currentPageId));
    },
    currentPage: (state) => {
      if (!state.draft?.pages) return null;
      return state.draft.pages.find((page) => (page.id === state.currentPageId));
    },
    pageIds: (state) => (state.draft?.pages || []).map((page) => page.id),
    currentPageAssetsLoaded() {
      const assetStore = useAssetStore();
      if (!this.currentPage) return false;
      return this.currentPage.modules
        .filter((mod) => mod.type === 'asset')
        .every((mod) => {
          const assetId = get(mod, 'options.asset_id');
          return !!assetStore.assets[assetId];
        });
    },
    draftAccessLevel: (state) => (state.accessLevelForDraft || state.draft?.access_level),
    draftLanguageCode() {
      return this.draft?.language?.code || 'eng';
    },
    draftType: (state) => state.draft?.options?.type,
    draftName(state) {
      const decodedName = decodeHTML(state.draft?.name);
      if (decodedName) return decodedName;

      let defaultName = this.draftType === 'board'
        ? i18next.t('New Board')
        : i18next.t('New Slideshow');

      if (this.draftType === 'lesson') {
        defaultName = i18next.t('New Lesson');
      }

      return defaultName;
    },
    draftAssetId: (state) => (creation) => {
      const draft = creation || state.draft;
      if (!draft || !draft.references) return null;

      // Find the first asset reference that isn't a pointer asset
      const reference = draft.references
        .find((ref) => ref.reference_type === 'asset' && ref.reference_subtype !== 'head');

      return get(reference, 'reference_id');
    },
    draftIsLesson() {
      return this.draftType === 'lesson';
    },
    draftIsSlides() {
      return this.draftType === 'slides';
    },
    draftIsBoard() {
      return this.draftType === 'board';
    },
    draftPointerAssetId: (state) => (draft) => {
      const creation = draft || state.draft;
      if (!creation) return null;
      return chain(creation.references)
        .find((ref) => ref.reference_subtype === 'head')
        .get('reference_id')
        .value();
    },
    draftIsDirty: (state) => get(state, 'session.draftIsDirty', false),
    draftIsDeCreated: (state) => get(state, 'draft.sync_info.is_de_created', false),
    draftCanBeRestored(state) {
      return !this.draftIsDeCreated && get(state, 'draft.is_deleted', false);
    },
    getModuleById: (state) => (id) => utils.getModuleById(state.draft, id),
    interactiveModules: (state) => utils.getInteractiveModules(state.draft),
    draftHasInteractiveModules: (state) => utils.draftHasInteractiveModules(state.draft),
    draftHasTEIErrors: (state) => values(state.teiErrors).some((error) => error),
    gridIsDisabled(state) {
      if (state.currentModalCanvasId) {
        // If a modal canvas is open, the grid is always disabled
        return true;
      }

      // Otherwise read the setting from the current page
      return get(this, 'currentPage.options.grid_disabled', false);
    },
    loadAssetsFromContentApi() {
      return this.studioAppIsRunning
        && get(this, 'draft.state') === 'draft'
        && this.region === 'US'
        && this.draftIsDeCreated
        && !getLocalData();
    },
    modalCanvases: (state) => (state.draft?.modals || []),
    modulesOrderedByZPosition() {
      return chain(this)
        .get('currentPage.modules', [])
        .cloneDeep()
        .sortBy(['layout.z_position', 'id'])
        .value();
    },
    getModulesByModalIdOrderedByZPosition: (state) => (modalId) => chain(state)
      .get('draft.modals')
      .find((modal) => modal.id === modalId)
      .get('modules', [])
      .cloneDeep()
      .sortBy(['layout.z_position', 'id'])
      .value(),
    getModulesByPageIdOrderedByZPosition: (state) => (pageId) => chain(state)
      .get('draft.pages')
      .find((page) => page.id === pageId)
      .get('modules', [])
      .cloneDeep()
      .sortBy(['layout.z_position', 'id'])
      .value(),
    getOrderedModules() {
      const mods = cloneDeep(this.currentPage?.modules);
      if (mods) {
        utils.sortModules(mods);
      }
      return mods;
    },
    getOrderedModulesByModalId: (state) => (modalId) => {
      const modal = get(state, 'draft.modals', []).find((m) => m.id === modalId);
      return modal
        ? utils.sortModules(cloneDeep(modal.modules))
        : [];
    },
    getOrderedModulesByPageId: (state) => (pageId) => {
      const page = get(state, 'draft.pages', []).find((draftPage) => draftPage.id === pageId);
      return page
        ? utils.sortModules(cloneDeep(page.modules))
        : [];
    },
    displayedPageId(state) {
      if (!this.pagesSorted || !isNumber(state.editorCarouselPageIndex)) return null;
      return this.pagesSorted[state.editorCarouselPageIndex]?.id;
    },
    displayedModuleIds(state) {
      if (!this.pagesSorted || !isNumber(state.editorCarouselPageIndex)) return new Set();
      const moduleIds = new Set();
      if (state.currentModalCanvasId) {
        const modal = get(state, 'draft.modals', []).find((m) => m.id === state.currentModalCanvasId);
        (modal?.modules || []).forEach((mod) => {
          moduleIds.add(mod.id);
        });
      }
      const modules = this.pagesSorted[state.editorCarouselPageIndex]?.modules || [];
      modules.forEach((mod) => {
        moduleIds.add(mod.id);
      });
      return moduleIds;
    },
    currentlyEditing: (state) => (componentId) => {
      if (!state.session || !state.session.lockedComponents) return null;
      const editing = state.session.lockedComponents[componentId];
      if (!editing) return null;
      return state.connectedUsers.find((u) => u.id === editing);
    },
    isLocked() {
      return (componentId) => {
        const currentlyEditingUser = this.currentlyEditing(componentId);
        return !!currentlyEditingUser && !currentlyEditingUser.isCurrentUser;
      };
    },
    currentUserConnection(state) {
      return find(state.connectedUsers, (user) => user.isCurrentUser);
    },
    isHost: (state) => {
      const hostUser = find(state.connectedUsers, (user) => user.isHost);
      const currentUser = find(state.connectedUsers, (user) => user.isCurrentUser);
      return hostUser && currentUser && currentUser.id === hostUser.id;
    },
    /**
      Picks a host user from the set of connected users that are currently viewing
      the current page. Returns true if the current user is the selected host user.
     */
    isCurrentPageHost() {
      const currentPageId = get(this, 'currentPage.id');
      return this.isPageHost(currentPageId);
    },
    /*
      Determines if the current user is the host for the provided page id.
    */
    isPageHost: (state) => (pageId) => {
      if (!pageId) return false;
      const pageHost = chain(state)
        .get('session.currentPageByUserId')
        .toPairs()
        .filter((pair) => last(pair) === pageId)
        .map((pair) => find(state.connectedUsers, (user) => user.id === first(pair)))
        .filter((user) => user)
        .sortBy('connectionTime')
        .first()
        .value();
      return get(pageHost, 'isCurrentUser', false);
    },
    isModuleClipboardEmpty(state) {
      return isEmpty(state.moduleClipboard);
    },
    eventsConfig() {
      /*
        Gets the configuration for how specific events will be handled in Studio.
        type:
          'document' - Event will dispatched on the document object (used on platform pages)
          'bus' - Event will be dispatched on the Studio event bus
          'undefined' - Event will be handled internally within Studio (default behavior)
        These event configurations can be overriden via the 'editorEvents' prop on StudioView.
      */
      const isPlatform = window.initData && window.location.href.includes('/learn');
      const config = {};
      if (isPlatform) {
        config['quicklist:add'] = { type: EVENT_TYPE_DOCUMENT };
        config['quicklist:remove'] = { type: EVENT_TYPE_DOCUMENT };
        config['channels:subscribe'] = { type: EVENT_TYPE_DOCUMENT };
        config['channels:unsubscribe'] = { type: EVENT_TYPE_DOCUMENT };
      }
      return merge(config, this.editorEvents);
    },
    eventType: (state) => (eventName) => state.eventsConfig[eventName]?.type,
    nextPage() {
      const pages = this.pagesSorted || [];
      const currentPageIdx = pages.findIndex((page) => (page.id === this.currentPageId));
      if (currentPageIdx === get(this, 'draft.pages.length') - 1) return null;
      if (this.editorIsFullscreen) {
        return chain(pages)
          .slice(currentPageIdx + 1)
          .find((page) => !get(page, 'options.fullscreen_skip'))
          .value() || null;
      }
      return pages[currentPageIdx + 1] || null;
    },
    previousPage() {
      const pages = this.pagesSorted || [];
      const currentPageIdx = pages.findIndex((page) => (page.id === this.currentPageId));
      if (currentPageIdx === 0) return null;
      if (this.editorIsFullscreen) {
        return chain(pages)
          .take(currentPageIdx)
          .findLast((page) => !get(page, 'options.fullscreen_skip'))
          .value() || null;
      }
      return pages[currentPageIdx - 1] || null;
    },
  },
  actions: {
    [types.SET_EDITOR_MENU_SETTINGS](id, menuSettings) {
      this.editorMenuSettings[id] = menuSettings;
    },
    [types.SET_EDITOR_EVENTS](editorEvents) {
      this.editorEvents = editorEvents;
    },
    [types.SET_EDITOR_CAROUSEL_IS_ANIMATED](isAnimated) {
      this.editorCarouselIsAnimated = isAnimated;
    },
    [types.SET_ASSIGNMENT_ID](id) {
      this.assignmentId = id;
    },
    [types.SET_IS_PREVIEW_MODE](isPreviewMode) {
      this.isPreviewMode = isPreviewMode;
    },
    [types.SET_OVERLAY_THUMBNAIL](url) {
      this.overlayThumbnail = url;
    },
    async [types.TOGGLE_EDITOR_DRAWER]({ drawer, open }) {
      // If we've passed in a new open state, use that. Otherwise, toggle the drawer state
      const newDrawerState = open === undefined
        ? !this.editorDrawers[drawer]
        : open;

      if (!newDrawerState) {
        const confirmationStore = useConfirmationStore();
        const confirmAction = `${types.TOGGLE_EDITOR_DRAWER}:${drawer}:close`;
        if (!(await confirmationStore[types.CONFIRM_ACTION](confirmAction))) {
          return;
        }
      }

      this.editorDrawers[drawer] = newDrawerState;
    },
    [types.SET_CURRENT_PAGE_FOR_CONNECTED_USER](pageId) {
      if (get(sharedDoc, 'isConnected')) {
        sharedDoc.change((yDoc) => {
          const currentUserId = get(sharedDoc, 'currentUser.id');
          if (currentUserId) {
            yDoc.get('session').get('currentPageByUserId').set(currentUserId, pageId);
          }
        }, LOCAL_TRANSACTION);
      }
    },
    /*
      IMPORTANT: Always use this action to set the current page id.
      commit(types.VIEW_PAGE_BY_ID) should not be called outsite this action
      because this action sets the current page for this user in the sharedDoc.
    */
    async [types.VIEW_PAGE_BY_ID](id) {
      if (id !== this.currentPageId) {
        const confirmationStore = useConfirmationStore();
        if (!(await confirmationStore[types.CONFIRM_ACTION](types.VIEW_PAGE_BY_ID))) {
          return;
        }
      }

      const pages = get(this, 'draft.pages', []);
      const newPage = pages.find((page) => page.id === id);
      if (newPage) {
        // Reset drawers if necessary
        if (newPage.options?.asset_id) {
          await this[types.TOGGLE_EDITOR_DRAWER]({ drawer: 'addModule', open: false });
        }

        this.currentPageId = id;
        this.currentModalCanvasId = null;
        this[types.SET_CURRENT_PAGE_FOR_CONNECTED_USER](id);
      } else {
        const firstPage = chain(pages)
          .sortBy('sort_index', 'id')
          .first()
          .value();
        if (firstPage) {
          // Reset drawers if necessary
          if (firstPage.options?.asset_id) {
            await this[types.TOGGLE_EDITOR_DRAWER]({ drawer: 'addModule', open: false });
          }

          this.currentPageId = firstPage.id;
          this.currentModalCanvasId = null;
          this[types.SET_CURRENT_PAGE_FOR_CONNECTED_USER](id);
        }
      }
    },
    async [types.VIEW_MODAL_CANVAS_BY_ID](id) {
      const confirmationStore = useConfirmationStore();
      if (!(await confirmationStore[types.CONFIRM_ACTION](types.VIEW_MODAL_CANVAS_BY_ID))) {
        return;
      }
      const modals = get(this, 'draft.modals', []);
      const newModal = modals.find((modal) => modal.id === id);
      if (newModal) {
        this.currentModalCanvasId = id;
      }
    },
    [types.CLOSE_MODAL_CANVAS]() {
      this.currentModalCanvasId = null;
    },
    async [types.VIEW_PREV_PAGE]() {
      if (this.previousPage) {
        await this[types.VIEW_PAGE_BY_ID](this.previousPage.id);
      }
    },
    async [types.VIEW_NEXT_PAGE]() {
      if (this.nextPage) {
        await this[types.VIEW_PAGE_BY_ID](this.nextPage.id);
      }
    },
    async [types.VIEW_CLOSEST_FULLSCREEN_VISIBLE_PAGE]() {
      const closestVisiblePage = this.closestFullscreenVisiblePage();
      if (closestVisiblePage) {
        await this[types.VIEW_PAGE_BY_ID](closestVisiblePage.id);
      }
    },
    async [types.SEND_API_REQUEST](patchList) {
      const toastStore = useToastStore();
      // get the draft id
      const draftId = this.draft?.id;

      if (draftId) {
        // toggle saving
        this[types.UPDATE_SAVING](true);

        try {
          // make api req
          await this.titanApiFetch(`/drafts/${draftId}`, {
            method: 'PATCH',
            body: patchList,
          });
        } catch (error) {
          const errorMessage = error?.response?.data?.meta?.message || error;
          toastStore[types.SET_STUDIO_TOAST]({ type: 'error', message: errorMessage });
        }

        // toggle saving
        this[types.UPDATE_SAVING](false);
      }

      // success. errors will reject the promise, which will propogate to the poll func
      return true;
    },
    [types.START_POLL]() {
      // curry
      const send = (patchList) => this[types.SEND_API_REQUEST](patchList);
      // hook em up
      queue.poll(send, 1000);
    },
    [types.KILL_POLL]() {
      // hook em up
      queue.clear();
      queue.kill();
    },
    [types.SET_STUDENT_VIEW](studentView) {
      this.studentView = studentView;
    },
    async [types.GET_TEMPLATES]() {
      const result = await this.titanApiFetch('/templates/');

      if (result && result.status === 200) {
        this.templates = result.data.templates;
      } else {
        // Error handling
        this.templates = [];
      }
    },
    [types.DISABLE_GRID_FOR_PAGE]({ pageId }) {
      if (!sharedDoc) return;
      sharedDoc.change((yDoc) => {
        const yPage = yjsHelper.find(
          yDoc.get('draft').get('pages'),
          (page) => page.get('id') === pageId,
        );

        if (yPage) {
          const yPageOptions = yPage.get('options');

          // Update the page options
          this[types.UPDATE_PAGE_OPTIONS]({
            options: {
              ...(yPageOptions
                ? yPageOptions.toJSON()
                : {}),
              grid_disabled: true,
            },
            pageId,
          });

          // Convert the page's modules from row/column layout to no-grid layout
          yPage.get('modules').forEach((yModule) => {
            const noGridLayout = layoutToPixels(yModule.get('layout').toJSON());
            yModule.set('layout', yjsHelper.toY(noGridLayout));
          });
        }
      }, LOCAL_TRANSACTION);
    },
    [types.CLEAR_DRAFT]() {
      if (this.draft) {
        /*
          Clearing the current draft will flag it as 'unloaded' which
          will prevent it from being displayed in the UI. Keeping the draft in
          memory prevents any temporary errors in computed properties that might
          occur throughout the app while a new draft is being loaded.
        */
        this.draft.unloaded = true;
      }
      this.accessLevelForDraft = null;
    },
    [types.CLOSE_EDITOR_DRAWERS]() {
      Object.keys(this.editorDrawers).forEach((drawer) => {
        this.editorDrawers[drawer] = false;
      });
    },
    async [types.GET_CREATION](payload = {}) {
      const appStore = useAppStore();
      const assetStore = useAssetStore();
      const assessmentStore = useAssessmentStore();
      const trackingStore = useTrackingStore();
      const errorStore = useErrorStore();
      const userStore = useUserStore();
      try {
        appStore[types.UPDATE_LOADING](true);
        this[types.CLOSE_EDITOR_DRAWERS]();

        const {
          allowAttempt,
          allowTeacherAttempt = true,
          homeworkId,
          id,
          preventRedirect,
          type,
          pageId,
          userId,
          ignoreGetAttemptError,
          useRevision,
        } = payload;

        let { creation } = payload;

        if (!id && !creation) {
          return;
        }

        if (!creation) {
          // generate the draft path
          let path;

          // Grab by reference id or creation id
          if (isGuid(id) || type) {
            path = `/creations/${id}?reference_type=${type || 'asset'}`;
          } else {
            path = `/creations/${id}?`;
          }

          /*
            grab the activity summary only if we are tracking metadata
            and the current user is authenticated.
          */
          if (
            (
              trackingStore.progressTracking?.enabled
              && !trackingStore.isAnonymous
              && userStore.user
            )
            || appStore.isOffline // when offline, we need to replicate the original call in order to pull from cache
          ) {
            path += '&activity_summary=1';
          }

          if (homeworkId) {
            path += `&assignment_id=${homeworkId}`;
          }

          // get the draft
          let result;

          try {
            result = await this.apiProgressBarFetch(path);

            if (!result?.status) {
              // When offline, the fetch api returns the error rather than throwing it.
              throw result;
            }
          } catch (e) {
            // if offline and has activity summary, retry without it
            if (appStore.isOffline || (navigator && !navigator?.onLine)) {
              // remove the activity summary query param and try again
              const activitySummaryQuery = '&activity_summary=1';
              const hasActivitySummaryQuery = path.includes(activitySummaryQuery);

              if (hasActivitySummaryQuery) {
                path = path.replace(activitySummaryQuery, '');
                result = await this.apiProgressBarFetch(path);
              }
            }
          }

          if (result?.status !== 200) {
            throw result;
          }

          creation = result.data.creation;
        }

        const creationRevisionAssetReference = creation.references
          .find((ref) => ref.reference_type === 'asset' && ref.reference_subtype !== 'head');
        const creationPointerAssetReference = creation.references
          .find((ref) => ref.reference_type === 'asset' && ref.reference_subtype === 'head');
        const isActivityTemplate = creation.options.is_template
          && creation.options.template_type === 'activity';

        // Redirect to a route with the pointer asset id instead of the revision asset id
        // unless we're viewing an assigned board or an activity template. This also
        // redirects if we've accessed a creation using its mongo id.
        if (!(isActivityTemplate && window.location.pathname.includes('activity-templates'))
          && !preventRedirect
          && creationPointerAssetReference
          && id !== creationPointerAssetReference.reference_id
          && !(
            creationRevisionAssetReference
              && id === creationRevisionAssetReference.reference_id
              && useRevision
          )) {
          const correctId = useRevision
            ? creationRevisionAssetReference.reference_id
            : creationPointerAssetReference.reference_id;

          window.location.href = window.location.href.replace(`id=${id}`, `id=${correctId}`);
          return;
        }

        // set the assignment id
        if (homeworkId) {
          this[types.SET_ASSIGNMENT_ID](homeworkId);
        }

        if (isActivityTemplate && !userStore.isDeUser
          && !window.location.pathname.includes('activity-templates')) {
          // Non-editorial users should not be able to view activity templates in the editor
          errorStore[types.SET_ERROR]({
            active: true,
            error: {
              component: 'ActivityTemplatePermissionsError',
              options: {
                creation,
              },
            },
          });
          return;
        }

        // allowAttempt can be overridden for teachers looking at lessons with full slide teis
        // regular studio quizzes cannot be interacted it without a homework guid
        const teacherAttemptOverride = (
          allowTeacherAttempt
          && !userStore.userIsStudent
          && creation.tei_page_asset_ids
        );
        const enableAttempts = teacherAttemptOverride || allowAttempt;

        if (!appStore.isOffline) {
          if (creationRevisionAssetReference && enableAttempts) {
            // For assigned boards with interactive modules or full-slide TEIs,
            // retrieve the attempt
            if (utils.draftHasInteractiveModules(creation) || creation.tei_page_asset_ids?.length) {
              await assessmentStore[types.GET_ATTEMPT]({
                assessmentId: creationRevisionAssetReference.reference_id,
                homeworkId,
                userId,
                ignoreError: ignoreGetAttemptError,
              });
            } else if (homeworkId) {
              try {
                // For assigned boards without interactive modules, get the assigned status
                const assignmentResponse = await this.deApi(`/assignments/${homeworkId}`);
                const assignmentStatus = get(assignmentResponse, 'data.status');

                if (COMPLETED_ASSIGNMENT_STATUSES.indexOf(assignmentStatus) !== -1) {
                  creation.assignmentIsComplete = true;

                  // disable tracking if the assignment is complete
                  trackingStore[types.DISABLE_ALL_EVENTS]();
                }

                // if the status is not-started and the viewer is the assignee, mark as started
                if (assignmentStatus === NOT_STARTED_ASSIGNMENT_STATUS) {
                  await this.deApi.post(
                    `/assignments/${homeworkId}/update_status`,
                    {
                      status_id: IN_PROGRESS_ASSIGNMENT_STATUS_ID,
                    },
                    {
                      headers: {
                        'X-Token': appStore.apiToken,
                      },
                    },
                  );
                }
              } catch (e) {
                // if you are a student that this board has not been assigned to, show an error
                if (e.response?.status === 400 && userStore.userIsStudent) {
                  errorStore[types.SET_ERROR]({
                    active: true,
                    error: e.response.data,
                  });
                }
              }
            }
          }
        }

        // disable events if the creation is not de-created
        if (!creation.sync_info.is_de_created) {
          trackingStore[types.DISABLE_ALL_EVENTS]();
        }

        // Set the draft and clear the draft access level to prevent hangover data
        this.draft = creation;
        this.accessLevelForDraft = null;
        if (pageId) {
          await this[types.VIEW_PAGE_BY_ID](pageId);
        } else {
          const pages = this.pagesSorted;
          if (pages && pages[0]) {
            await this[types.VIEW_PAGE_BY_ID](pages[0].id);
          }
        }
        assetStore[types.CACHE_ASSETS_AND_UPLOADS]();
        assessmentStore[types.CACHE_TEIS]();

        // Send a clicklog event for the creation
        if (creationPointerAssetReference) {
          trackingStore[types.SEND_CLICKLOG_EVENT]({
            ids: [creationPointerAssetReference.reference_id],
          });
        }

        // store the activity summary
        if (this.draft.activity_summary && !trackingStore.isAnonymous) {
          trackingStore[types.SET_ACTIVITY_SUMMARY](this.draft.activity_summary);
        }
      } catch (e) {
        // Error handling
        this[types.CLEAR_DRAFT]();
        errorStore[types.SET_ERROR]({
          active: true,
          error: e?.response?.data || e,
        });
      } finally {
        appStore[types.UPDATE_LOADING](false);
      }
    },
    async [types.MARK_AS_COMPLETE]({ homeworkId }) {
      const appStore = useAppStore();
      if (!homeworkId || appStore.isOffline) return;
      try {
        const response = await this.deApi.post(`/assignments/${homeworkId}/update_status`, {
          status_id: 2, // SUBMITTED
        }, {
          headers: {
            'X-Token': appStore.apiToken,
          },
        });

        if (response.status === 200) {
          this.draft.assignmentIsComplete = true;
        } else {
          throw response;
        }
      } catch (e) {
        const errorMessage = get(e, 'response.data.meta.message', e);
        const toastStore = useToastStore();
        toastStore[types.SET_STUDIO_TOAST]({ type: 'error', message: errorMessage });
      }
    },
    async [types.CREATE_DRAFT](payload) {
      const appStore = useAppStore();
      const errorStore = useErrorStore();
      let newDraft;
      const {
        isTemplate,
        originAssetId,
        pages,
        type,
      } = payload;
      appStore[types.UPDATE_LOADING](true);

      const postUrl = originAssetId ? `/drafts/?origin_asset_id=${originAssetId}` : '/drafts/';

      // Create the draft
      const result = await this.apiProgressBarFetch(postUrl,
        {
          method: 'POST',
          body: {
            name: utils.defaultDraftName(type),
            options: {
              type,
              is_template: isTemplate || undefined,
              template_type: isTemplate ? 'activity' : undefined,
            },
            pages: pages || [utils.newPageTemplate(type)],
          },
          headers: {
            'X-Token': appStore.apiToken,
          },
        });

      // Error handling
      if (result.status === 200) {
        appStore[types.UPDATE_LOADING](false);
        newDraft = result.data.draft;
      } else {
        errorStore[types.SET_ERROR]({
          active: true,
          error: get(result, 'response.data'),
        });
      }

      return newDraft;
    },
    async [types.COPY_CREATION](payload) {
      const appStore = useAppStore();
      const assetStore = useAssetStore();
      const assessmentStore = useAssessmentStore();

      const {
        type,
        id,
        commitOnSuccess,
        title,
        editOnSuccess,
      } = payload;

      let newDraft;
      const overrides = {};

      // let's craft a name
      if (title) {
        overrides.name = title;
      } else if (this.draft?.name) {
        let newTitle = '';
        const nameRegex = /^Copy \(([0-9]+)\) (.*?)$/i;
        const nameMatch = this.draft.name.match(nameRegex);

        if (nameMatch) {
          newTitle = `Copy (${parseInt(nameMatch[1], 10) + 1}) ${nameMatch[2]}`;
        } else {
          newTitle = `Copy (1) ${this.draft.name}`;
        }

        // Make a new board title. i18next escapes its strings by default, but since
        // the BE already escapes the board title once, this prevents it from being
        // double-escaped
        let copiedName = i18next.t('%(boardTitle)s', {
          boardTitle: newTitle,
          interpolation: { escapeValue: false },
        });

        if (copiedName.length >= 100) {
          // Prevent board names longer than 100 characters, since those will
          // have trouble being published or assigned
          copiedName = truncate(copiedName, { length: 100 });
        }

        // add the name to overrides
        overrides.name = copiedName;
      }

      // Create a new draft based on the passed id
      const result = await this.titanApiFetch(
        `/drafts/?source_${type}_id=${id}`, // e.g. '/drafts/?source_creation_id=GUID'
        {
          method: 'POST',
          body: overrides,
          headers: {
            'X-Token': appStore.apiToken,
          },
        },
      );

      if (result.status === 200) {
        newDraft = result.data.draft;
      } else {
        // Error handling
        throw result;
      }

      if (commitOnSuccess) {
        this.draft = newDraft;
        const pages = this.pagesSorted;
        this[types.VIEW_PAGE_BY_ID](pages[0].id);
        assetStore[types.CACHE_ASSETS_AND_UPLOADS]();
        assessmentStore[types.CACHE_TEIS]();
      }

      if (editOnSuccess) {
        // grab the new pointer
        const reference = newDraft.references.find(ref => ref.reference_subtype === 'head');

        // open editing session
        if (reference) {
          await this[types.OPEN_EDITING_SESSION]({
            draftId: reference.reference_id,
            pageId: newDraft.pages[0].id,
          });
        }
      }

      return newDraft;
    },
    async [types.RESTORE_CREATION](payload) {
      let url;
      if (!payload?.creation) {
        const editorStore = useEditorStore();

        const id = editorStore.draftAssetId();
        const referenceType = 'asset';

        url = `/drafts/${id}?reference_type=${referenceType}`;
      } else {
        const { creation, type = 'creation' } = payload;
        url = type === 'creation'
          ? `/creations/${creation.id}?reference_type=asset`
          : `/drafts/${creation.id}?reference_type=asset`;
      }

      // eslint-disable-next-line no-restricted-syntax
      const result = await this.titanApiFetch(url, {
        method: 'PATCH',
        body: [{
          op: 'replace',
          path: '/is_deleted',
          value: false,
        }],
      });
      if (result.status !== 200) throw result;
    },
    [types.SET_ACTIVE_CANVAS_NAME](canvasName) {
      this.activeCanvasName = canvasName;
    },
    [types.SET_EDITOR_CAROUSEL_PAGE_INDEX](pageIndex) {
      this.editorCarouselPageIndex = pageIndex;
    },
    async [types.COPY]({
      assetId,
      modules,
      pageIds,
      canvasName,
      toClipboard = true,
    }) {
      // If there is no data to copy, then exit.
      if (
        (modules?.length && !canvasName)
        || (!modules?.length && !pageIds?.length && !assetId)
      ) return null;

      const toastStore = useToastStore();
      let modalCopies = [];
      let copyData = null;

      // Copy an asset id to the device and module clipboard.
      if (assetId) {
        copyData = {
          assetId,
        };
        if (toClipboard) {
          this.moduleClipboard = copyData;
          await navigator.clipboard.writeText(assetId);
        }
      }

      // Copy modules to the device and module clipboard.
      if (modules?.length) {
        const modulesCopy = cloneDeep(modules);

        /*
          Make copies of any modal canvases used by these modules and
          add them to the device and module clipboard.
        */
        modalCopies = uniqBy(
          flatten(modulesCopy.map((m) => getModalsForModule(m))),
          (copy) => copy.id,
        );

        copyData = {
          canvasName,
          type: APP_COPY_DATA_TYPE,
          modules: modulesCopy,
          modals: modalCopies,
          modulesPageId: this.currentPage.id,
          draftId: this.draft.id,
        };

        if (toClipboard) {
          this.moduleClipboard = copyData;
          await navigator.clipboard.writeText(JSON.stringify(copyData));
          toastStore[types.SET_STUDIO_TOAST]({
            type: 'success',
            position: 'top',
            message: i18next.t('Copied to Clipboard!'),
          });
        }
      }

      // Copy pages to the device clipboard.
      if (pageIds) {
        const pages = [];
        uniq(pageIds).forEach((pageId) => {
          const page = this.draft.pages.find((p) => p.id === pageId);
          if (!page) return;
          pages.push(cloneDeep(page));
        });

        /*
          Make a copy of any modal canvases used by modules within
          this page and add them to the device and module clipboard.
        */
        modalCopies = getModalsForPages(pages);

        if (pages?.length) {
          copyData = {
            type: APP_COPY_DATA_TYPE,
            draftType: this.draftType,
            pages,
            modals: modalCopies,
          };

          if (toClipboard) {
            await navigator.clipboard.writeText(JSON.stringify(copyData));
            // These l10n terms don't have interpolation but they will still use
            // a plural term if we pass in `count`
            const message = this.draftType === 'board'
              ? i18next.t('Page copied.', { count: pages.length })
              : i18next.t('Slide copied.', { count: pages.length });
            toastStore[types.SET_STUDIO_TOAST]({
              type: 'success',
              position: 'top',
              message,
            });
          }
        }
      }

      return copyData;
    },
    async [types.PASTE]({
      canvasName,
      copyData,
      moduleToReplace,
      getModulePosition,
      onFilesUploaded,
    }) {
      // Don't allow paste onto the main canvas if this is a full slide asset.
      const isAssetSlide = !!this.currentPage?.options?.asset_id;
      if (isAssetSlide && canvasName === 'modules') {
        return null;
      }

      // Paste from provided copyData or from the module clipboard.
      const pasteData = copyData || this.moduleClipboard;

      // If paste data is not valid, then exit with a message.
      if (!validatePasteData({ pasteData, canvasName, moduleToReplace })) {
        return null;
      }

      const assetStore = useAssetStore();
      const toastStore = useToastStore();
      const userStore = useUserStore();
      const modalStore = useModalStore();

      let moduleCopies = null;
      let modalCopies = null;
      let pageCopies = null;
      let modalCopyMap = null;

      // Create an undo stack item to hold changes for the paste.
      const pasteItem = new DocumentChangeStackItem(sharedDoc);

      // Copies the modals on the clipboard and adds them to the draft.
      const copyModalsToStore = () => {
        const result = this[types.COPY_MODAL_CANVASES]({
          modals: pasteData.modals,
          undoItem: pasteItem,
        });
        modalCopyMap = result.modalCopyMap;
        modalCopies = result.modalCopies;
      };

      /*
        If there are files on the clipboard, then open
        the appropriate upload modal.
      */
      if (pasteData.files?.length) {
        if (userStore.isDeUser) {
          modalStore[types.OPEN_MODAL]({
            type: 'EditorialUploadInfo',
            allowMultiple: true,
            canvasName,
            initFiles: [...pasteData.files],
          });
        } else {
          modalStore[types.OPEN_MODAL]({
            type: 'MyContentUploadModal',
            allowMultiple: true,
            baseURL: this.domains.deDomain,
            initFiles: [...pasteData.files],
            onAssetsUploaded: onFilesUploaded,
          });
        }
        return {
          files: pasteData.files,
        };
      }

      /*
        If we are pasting an asset id, get the asset from the server
        and set the modules on the pasteData.
      */
      if (pasteData.assetId) {
        const assetModule = await getPasteModuleFromAssetId({
          pasteData, canvasName,
        });
        pasteData.modules = [assetModule];
      }

      // Only paste the modules that are allowed on the target canvas.
      pasteData.modules = getPastableModules({
        pasteData,
        canvasName,
      });

      // Paste modules from the clipboard.
      if (pasteData.modules?.length) {
        // Only paste the modals that are being used by the pastable modules.
        const modalsForModule = (mod) => getModalsForModule(mod, pasteData.modals);
        pasteData.modals = uniqBy(
          flatten(pasteData.modules.map(modalsForModule)),
          (modal) => modal.id,
        );

        // If we're not pasting to a modal canvas, then copy modals.
        if (canvasName !== 'modalModules') {
          copyModalsToStore();
        }

        /*
          Make a copy of each pastable module and map
          its modal ids to the copied modals.
        */
        moduleCopies = copyModules({
          modules: pasteData.modules,
          modalCopyMap,
        });

        /*
          When pasting to and from these canvases, preserve the relative
          z order for the pasted modules unless we're replacing a
          module.
        */
        const useZOrderCanvases = ['modules', 'modalModules'];
        const preserveZOrder = useZOrderCanvases.includes(canvasName)
          && useZOrderCanvases.includes(pasteData.canvasName)
          && !moduleToReplace;

        if (preserveZOrder) {
          // If preserving z order, sort pasted modules by z position.
          moduleCopies = sortBy(moduleCopies, (m) => m.layout?.z_position);
        } else {
          // Otherwise, sort by their position on the canvas.
          moduleCopies = sortBy(moduleCopies.map((m) => ({
            mod: m,
            x: m.layout?.x_position,
            y: m.layout?.y_position,
          })), ['y', 'x']).map((item) => item.mod);
        }

        // Set the module options for each pasted module.
        setPastedModulesOptions({ modules: moduleCopies, canvasName });

        if (moduleToReplace) {
          // Replace the module with the first copy in sorted order.
          const moduleCopy = first(moduleCopies);

          // Set the layout to the layout for the module being replaced.
          moduleCopy.layout = cloneDeep(moduleToReplace.layout);

          // Replace the module with the pasted module.
          moduleCopy.id = await this[types.REPLACE_MODULE]({
            canvasName,
            payload: moduleCopy,
            replacedModuleId: moduleToReplace.id,
            undoItem: pasteItem,
          });

          /*
            Update the moduleCopies list to only contain the
            replacement module.
          */
          moduleCopies = [moduleCopy];
        } else {
          if (isPasteChangingLayout({
            canvasName,
            sourceCanvasName: pasteData.canvasName,
          })) {
            /*
              If we're changing coordinate systems, then
              set the default module layouts for the target
              coordinate system.
            */
            setPastedModulesDefaultLayout({
              modules: moduleCopies,
              canvasName,
            });
          }

          /*
            Set the layout object on the pasted modules. This
            will position each pasted module on the canvas.
          */
          setPastedModulesLayout({
            modules: moduleCopies,
            canvasName,
            sourceCanvasName: pasteData.canvasName,
            sourceModulesPageId: pasteData.modulesPageId,
            sourceDraftId: pasteData.draftId,
            getModulePosition,
          });

          // Move existing modules on paste if needed.
          const updateModules = updateModulesOnPaste({
            canvasName,
            pasteData,
            pastedModules: moduleCopies,
          });

          /*
            Add new pasted modules and move any existing modules
            all in one undoable transaction.
          */
          const { addModuleIds } = await this[types.ADD_UPDATE_DELETE_MODULES]({
            addModules: moduleCopies,
            updateModules,
            canvasName,
            undoItem: pasteItem,
          });

          // Assign new ids to the pasted modules.
          addModuleIds.forEach((moduleId, index) => {
            moduleCopies[index].id = moduleId;
          });
        }

        // Set panel to open when undoing or redoing this paste.
        pasteItem.tags.set('panel', { name: canvasName });

        // Apply changes and add to undo stack.
        applyUndoable(pasteItem);

        // Select the modules after pasting them on the canvas.
        defer(() => {
          bus.emit('grid-layout:set-selection', {
            ids: moduleCopies.map((m) => m.id),
            canvasName,
          });
        });
      }

      // Paste an array of pages from the clipboard.
      if (pasteData.pages?.length) {
        const copyResult = await this[types.COPY_PAGES]({
          pages: pasteData.pages,
          modals: pasteData.modals,
          undoItem: pasteItem,
        });
        pageCopies = copyResult.pageCopies;
        modalCopies = copyResult.modalCopies;

        // Set panel to open when undoing or redoing this paste.
        pasteItem.tags.set('panel', { name: 'pageDrawer' });

        // Apply changes and add to undo stack.
        applyUndoable(pasteItem);

        // Go to the first copied page.
        this[types.VIEW_PAGE_BY_ID](first(pageCopies).id);

        // These l10n terms don't have interpolation but they will still use
        // a plural term if we pass in `count`
        toastStore[types.SET_STUDIO_TOAST]({
          type: 'success',
          position: 'top',
          message: this.draftType === 'board'
            ? i18next.t('Page pasted.', { count: pageCopies.length })
            : i18next.t('Slide pasted.', { count: pageCopies.length }),
        });
      }

      // Load assets for any new objects pasted to the draft.
      assetStore[types.CACHE_ASSETS_AND_UPLOADS]();

      return {
        modules: moduleCopies,
        modals: modalCopies,
        pages: pageCopies,
      };
    },
    async [types.DUPLICATE_MODULES]({ modules, canvasName }) {
      const copyData = await this[types.COPY]({
        modules,
        canvasName,
        toClipboard: false,
      });

      await this[types.PASTE]({
        canvasName,
        copyData,
      });
    },
    [types.SET_EDITOR_STATUS_MESSAGE](payload) {
      this.editorStatusMessage = payload;
    },
    [types.CACHE_URL_REQUEST](payload) {
      this.urlRequestCache[payload.url] = payload.promise;
    },
    [types.TRACK_THUMBNAIL_LOADER](payload) {
      this.thumbnailLoaders.push(payload);
    },
    [types.CLEAR_THUMBNAIL_LOADERS]() {
      this.thumbnailLoaders = [];
    },
    [types.SET_TRACK_THUMBNAIL_LOADERS](track) {
      this.trackThumbnailLoaders = track;
    },
    [types.UPDATE_TEI_ERRORS]({ id, error }) {
      this.teiErrors[id] = error;
    },
    [types.SET_EDITOR_IS_FULLSCREEN](payload) {
      const modalStore = useModalStore();
      // Close modals when entering or exiting fullscreen mode
      modalStore[types.CLEAR_MODALS]();
      modalStore[types.SET_MY_CONTENT_MODAL_OPTIONS]({
        config: {
          isActive: false,
        },
      });

      // Toggle view mode preview on and off if in edit mode
      if (payload.mode === 'EDIT' || (!payload.isFullscreen && this.editorViewModePreview)) {
        this[types.SET_EDITOR_VIEW_MODE_PREVIEW](payload.isFullscreen);
      }

      this.editorIsFullscreen = payload.isFullscreen;
    },
    [types.SET_EDITOR_VIEW_MODE_PREVIEW](editorViewModePreview) {
      this.editorViewModePreview = editorViewModePreview;
    },
    [types.SET_EDITOR_SNAP_GRID](snapGrid) {
      this.editorSnapGrid = snapGrid;
    },
    [types.SET_TOP_NAV_DROPDOWN_IS_OPEN](payload) {
      this.topNavDropdownIsOpen = payload.isOpen;
    },
    [types.SET_TOP_NAV_DRAWER_IS_OPEN](isOpen) {
      this.topNavDrawerIsOpen = isOpen;
    },
    async [types.DELETE_DRAFT](payload) {
      const user = useUserStore();
      const { id } = payload;
      let referenceType = payload.referenceType || 'asset';

      // object ids need to use the creation reference type
      if (id.length < 30) {
        referenceType = 'creation';
      }

      const urls = [
        `/drafts/${id}?reference_type=${referenceType}`,
      ];

      // check the api to see if there is a creation for ucc users
      if (!user.isDeUser) {
        try {
          const creation = await this.titanApiFetch(`/creations/${id}?reference_type=${referenceType}`, {
            method: 'HEAD',
          });
          if (creation.status === 200) {
            urls.push(`/creations/${id}?reference_type=${referenceType}`);
          }
        } catch {
          // If the creations request 404s, do nothing
        }
      }

      // eslint-disable-next-line no-restricted-syntax
      for (const url of urls) {
        // eslint-disable-next-line no-await-in-loop
        try {
          // eslint-disable-next-line no-await-in-loop
          await this.titanApiFetch(url, {
            method: 'DELETE',
          });
        } catch (error) {
          /*
            If the resource doesn't exist or is already deleted, suppress the error.
            e.g. The associated creation may not exist. In this case continue with
            deleting the draft.
          */
          const errorStatus = error?.status || get(error, 'response.status');
          if (![404, 410].includes(errorStatus)) {
            throw error;
          }
        }
      }
    },
    /*
      **********************************************************************************
      Collaborative editing actions.
      TODO: Eventually, we will want to refactor the Editor into its own
      module that will contain these actions plus any others that
      are specific to the editor.
      Due to time constraints, this refactor will come at a later date and
      the editor and shared editing session will remain in the root module for now.
      **********************************************************************************
    */
    async [types.INIT_DRAFT](payload) {
      const { draft, pageId } = payload;
      const chatStore = useChatStore();
      const assetStore = useAssetStore();
      const assessmentStore = useAssessmentStore();
      this.draft = draft;
      this[types.SET_CONNECTED_USERS_COLORS](); // save the owner's editing color

      // get the pages sorted by sort_index
      const pages = this.pagesSorted;

      if (pageId && pages.find((page) => page.id === pageId)) {
        await this[types.VIEW_PAGE_BY_ID](pageId);
      } else {
        await this[types.VIEW_PAGE_BY_ID](pages[0].id);
      }
      assetStore[types.CACHE_ASSETS_AND_UPLOADS]();
      assessmentStore[types.CACHE_TEIS]();

      // By default, drafts have chat turned off. If it's already on, though,
      // we can open a chat session as well
      const chatEnabled = get(draft, 'options.chat_enabled');
      if (chatEnabled) {
        chatStore[types.GET_CHAT]({ draft });
      }
      chatStore[types.SET_CHAT_TOGGLE_ENABLED](chatEnabled);
    },
    async [types.GET_DRAFT]({
      draftId,
      isThumbnailGenerator,
      commitOnSuccess,
      pageId,
      type,
    }) {
      const userStore = useUserStore();
      const assetStore = useAssetStore();
      const assessmentStore = useAssessmentStore();
      const errorStore = useErrorStore();
      try {
        const path = (isGuid(draftId) || type)
          ? `/drafts/${draftId}?reference_type=${type || 'asset'}`
          : `/drafts/${draftId}`;

        const result = await this.apiProgressBarFetch(path);

        if (get(result, 'status') === 200) {
          const { draft } = result.data;

          // If there aren't any pages, add one
          if (!draft.pages || draft.pages.length === 0) {
            draft.pages = [utils.newPageTemplate()];

            const patch = {
              op: 'replace',
              path: '/pages',
              value: draft.pages,
            };

            await this.titanApiFetch(`/drafts/${draft.id}`, {
              method: 'PATCH',
              body: [patch],
            });
          }

          if (!draft.modals) {
            draft.modals = [];
          }

          if (draft.options.is_template && draft.options.template_type === 'activity'
            && !userStore.isDeUser && !window.location.pathname.includes('activity-templates')) {
            // Non-editorial users should not be able to edit activity templates
            const error = new Error();
            error.response = {
              data: {
                component: 'ActivityTemplatePermissionsError',
                options: {
                  creation: draft,
                },
              },
            };
            throw error;
          }

          // Make sure the draft is editable by the user
          if (['view', 'readonly'].includes(draft.access_level)) {
            const error = new Error('User not authorized to edit this draft');

            const draftPointerAssetReference = draft.references
              .find((ref) => ref.reference_type === 'asset' && ref.reference_subtype === 'head');

            error.response = {
              data: {
                component: 'ViewPermissionsError',
                options: {
                  draftReferenceId: draftPointerAssetReference
                    && draftPointerAssetReference.reference_id,
                  draftType: draft.options.type,
                },
              },
            };
            throw error;
          }

          if (isThumbnailGenerator || commitOnSuccess) {
            // The thumbnail generator doesn't load drafts in collaborative editing mode;
            // we can set the draft directly without dealing with a yDoc
            this.draft = draft;
            if (pageId) {
              await this[types.VIEW_PAGE_BY_ID](pageId);
            } else {
              const pages = this.pagesSorted;
              await this[types.VIEW_PAGE_BY_ID](pages[0].id);
            }
            assetStore[types.CACHE_ASSETS_AND_UPLOADS]();
            assessmentStore[types.CACHE_TEIS]();
          }

          return draft;
        }
        throw result;
      } catch (error) {
        errorStore[types.SET_ERROR]({
          active: true,
          error: get(error, 'response.data', error),
        });
        throw error;
      }
    },
    async [types.GET_DRAFT_REFERENCES]({ draftId, type }) {
      const path = (isGuid(draftId) || type)
        ? `/drafts/${draftId}?reference_type=${type || 'asset'}`
        : `/drafts/${draftId}`;
      const result = await this.titanApiFetch(path, {
        headers: { 'X-Fields': 'is_deleted, references, access_level, id, options{type}, metadata{published_on}' },
      });
      if (get(result, 'status') === 200) {
        // All users work off the same yDoc, but since their access levels are
        // different, this must be stored separately
        this.accessLevelForDraft = result.data.draft.access_level;
        return result.data.draft;
      }
      throw result;
    },
    async [types.GET_SHARED_DOCUMENT](payload) {
      const draft = await this[types.GET_DRAFT](payload);
      const sharedDocData = {
        session: {
          lockedComponents: {},
          draftIsDirty: false,
          currentPageByUserId: {},
        },
        draft,
      };
      return sharedDocData;
    },
    [types.UPDATE_MODULE]({
      canvasName,
      payload,
      captureTimeout,
      canUndo = true,
      undoItem,
    }) {
      this[types.UPDATE_MODULES]({
        canvasName,
        modules: [payload],
        captureTimeout,
        canUndo,
        undoItem,
      });
    },
    [types.UPDATE_MODULES]({
      canvasName,
      modules = [],
      captureTimeout,
      canUndo = true,
      undoItem,
    }) {
      // Create an undo item for the updates.
      const updateItem = undoItem || new DocumentChangeStackItem(sharedDoc, { captureTimeout });
      const isStandaloneTransaction = !undoItem;

      // Create an undoable change for module to be updated.
      modules.forEach((updatedModule) => {
        // Make a copy first.
        const mod = cloneDeep(updatedModule);

        // Make sure non-standard values are not present
        if (mod.options.display) {
          delete mod.options.display;
        }

        const getYModule = partial(utils.findYModuleById, canvasName);
        const yModule = getYModule(sharedDoc.document, mod.id);

        if (yModule) {
          const pageOrModalId = yModule.parent.parent.get('id');
          updateItem.tags.set('pageOrModalId', pageOrModalId);
          updateItem.add(DocumentChange.sync(getYModule, mod));
        }
      });

      if (canvasName === 'modalModules') {
        // Clear the copy source_id from the modal
        this[types.CLEAR_MODAL_CANVAS_SOURCE_ID]({ undoItem: updateItem });
      }

      if (isStandaloneTransaction) {
        // Set the panel to open when undoing or redoing this change.
        updateItem.tags.set('panel', { name: canvasName });

        const transactionOrigin = canUndo
          ? LOCAL_TRANSACTION
          : LOCAL_TRANSACTION_NOT_UNDOABLE;

        // Apply the changes to the yDoc.
        updateItem.apply(transactionOrigin);

        // If we are allowing undo, then add to undo manager.
        if (canUndo) {
          applyUndoable(updateItem);
        }
      }
    },
    async [types.ADD_UPDATE_DELETE_MODULES]({
      addModules = [],
      updateModules = [],
      deleteModules = [],
      canvasName,
      undoItem,
      canUndo = true,
    }) {
      // Create an undo item for the updates.
      const updateItem = undoItem || new DocumentChangeStackItem(sharedDoc);
      const isStandaloneTransaction = !undoItem;

      // Add module updates to the transaction.
      if (updateModules?.length) {
        this[types.UPDATE_MODULES]({
          canvasName,
          modules: updateModules,
          undoItem: updateItem,
        });
      }

      // Add delete module changes to the transaction.
      if (deleteModules?.length) {
        this[types.DELETE_MODULES]({
          canvasName,
          moduleIds: deleteModules.map((m) => m.id),
          undoItem: updateItem,
        });
      }

      // Add any specified new modules to the transaction.
      const addPromises = addModules.map((addModule) => {
        const addPromise = this[types.ADD_MODULE]({
          payload: cloneDeep(addModule),
          canvasName,
          undoItem: updateItem,
        });
        return addPromise;
      });

      // Wait for the new ids for added modules.
      const addModuleIds = await Promise.all(addPromises);

      if (isStandaloneTransaction) {
        // Set the panel to open when undoing or redoing this change.
        updateItem.tags.set('panel', { name: canvasName });

        const transactionOrigin = canUndo
          ? LOCAL_TRANSACTION
          : LOCAL_TRANSACTION_NOT_UNDOABLE;

        // Apply the changes to the yDoc.
        updateItem.apply(transactionOrigin);

        // If we are allowing undo, then add to undo manager.
        if (canUndo) {
          applyUndoable(updateItem);
        }
      }

      return {
        addModuleIds,
      };
    },
    [types.UPDATE_CURRENTLY_EDITING](changes) {
      if (!sharedDoc) return;
      sharedDoc.change((doc) => {
        Object.keys(changes).forEach((componentId) => {
          doc.get('session').get('lockedComponents')
            .set(componentId, changes[componentId]);
        });
      });
    },
    [types.CLEAR_CURRENT_USER_EDITING]() {
      if (!sharedDoc) return;
      sharedDoc.change((yDoc) => {
        // find any modules currently being edited by the user
        const currentUserId = this.currentUserConnection?.id;
        const lockedComponents = yDoc.get('session').get('lockedComponents');
        lockedComponents.forEach((value, key) => {
          // if the user matches and the key is a module id (uuid), then nullify
          if (value === currentUserId && uuidValidate(key)) {
            lockedComponents.set(key, null);
          }
        });
      });
    },
    [types.SET_ADD_MODULE_DRAWER_IS_OPEN](isOpen) {
      this.addModuleDrawerIsOpen = isOpen;
    },
    async [types.ADD_MODULE]({
      canvasName,
      payload,
      undoItem,
    }) {
      // Create an undo item for the add change.
      const addItem = undoItem || new DocumentChangeStackItem(sharedDoc);
      const isStandaloneTransaction = !undoItem;

      // generate a module
      const newModule = utils.newModuleTemplate(payload, canvasName);

      const pageOrModalId = canvasName === 'modalModules'
        ? this.currentModalCanvasId
        : this.currentPageId;

      // Create a function to get the canvas we're adding to.
      const getCanvas = partial(utils.findCanvasByPageOrModalId, canvasName, pageOrModalId);
      const yCanvas = getCanvas(sharedDoc.document);

      if (yCanvas) {
        // Create a change to add the module that is undoable.
        addItem.add(DocumentChange.add(getCanvas, newModule));
        addItem.tags.set('pageOrModalId', yCanvas.parent?.get('id'));

        // Clear the copy source_id from the modal
        if (canvasName === 'modalModules') {
          this[types.CLEAR_MODAL_CANVAS_SOURCE_ID]({ undoItem: addItem });
        }

        if (isStandaloneTransaction) {
          // Set the panel that should open when undoing/redoing this change.
          addItem.tags.set('panel', { name: canvasName });
          // Apply changes and allow undo.
          applyUndoable(addItem);
        }
      }

      return newModule.id;
    },
    async [types.REPLACE_MODULE]({
      canvasName,
      payload,
      replacedModuleId,
      undoItem,
    }) {
      // Create an undo item for the changes.
      const replaceItem = undoItem || new DocumentChangeStackItem(sharedDoc);
      const isStandaloneTransaction = !undoItem;

      // Create the new module that will replace the existing one.
      const replacementModule = utils.newModuleTemplate(payload, canvasName);

      replaceItem.onAdded = (manager) => {
        /**
         * Whenever we replace a module, the new module will have a different id than the existing
         * module that was replaced. When we clear undo history for a module that was a replacement
         * for another module, we also want to clear the undo history for original module that
         * was replaced. In order to do this, after we replace a module, we need to tag all the
         * stack items for the original module with the new module's id. When we clear history
         * for the replacement module, it will then clear the history for the original module as
         * well.
         */
        [...manager.undoStack, ...manager.redoStack].forEach((stackItem) => {
          /*
            If this stack item is related to the replaced module, then add a tag
            for the replacement module which will link them together.
          */
          if (stackItem.tags.get(replacedModuleId)) {
            if (!stackItem.tags.get(replacementModule.id)) {
              const replacementTags = replaceItem.tags.get(replacementModule.id);
              stackItem.tags.set(replacementModule.id, replacementTags);
            }
          }
        });
      };

      const pageOrModalId = canvasName === 'modalModules'
        ? this.currentModalCanvasId
        : this.currentPageId;

      // Create a function to get the canvas we're adding to.
      const getCanvas = partial(utils.findCanvasByPageOrModalId, canvasName, pageOrModalId);
      const yCanvas = getCanvas(sharedDoc.document);
      const yReplacedModule = utils.findYModuleById(canvasName,
        sharedDoc.document, replacedModuleId);

      if (yCanvas && yReplacedModule) {
        // Create a change to delete the replaced module.
        replaceItem.add(DocumentChange.delete(getCanvas, yReplacedModule.toJSON()));

        // Create a change to add the replacement module.
        replaceItem.add(DocumentChange.add(getCanvas, replacementModule));

        // Clear the copy source_id from the modal
        if (canvasName === 'modalModules') {
          this[types.CLEAR_MODAL_CANVAS_SOURCE_ID]({ undoItem: replaceItem });
        }

        // Set tags for stack item
        replaceItem.tags.set('pageOrModalId', yCanvas.parent?.get('id'));

        if (isStandaloneTransaction) {
          // Set the panel that should open to view this undo/redo change.
          replaceItem.tags.set('panel', { name: canvasName });
          // Apply the changes and add to undo manager.
          applyUndoable(replaceItem);
        }
      }

      return replacementModule.id;
    },
    [types.DELETE_MODULES]({
      canvasName,
      moduleIds,
      undoItem,
    }) {
      // Create undo item for delete
      const deleteItem = undoItem || new DocumentChangeStackItem(sharedDoc);
      const isStandaloneTransaction = !undoItem;

      // Add delete module changes to the undo item.
      moduleIds.forEach((moduleId) => {
        this[types.DELETE_MODULE]({
          id: moduleId,
          canvasName,
          undoItem: deleteItem,
        });
      });

      if (isStandaloneTransaction) {
        // Get the page or modal id depending on the current canvas.
        const pageOrModalId = canvasName === 'modalModules'
          ? this.currentModalCanvasId
          : this.currentPageId;

        // Set tags on the undo item.
        deleteItem.tags.set('panel', { name: canvasName });
        deleteItem.tags.set('pageOrModalId', pageOrModalId);

        // Apply changes and add to undo manager.
        applyUndoable(deleteItem);
      }
    },
    [types.DELETE_MODULE]({
      canvasName,
      id,
      undoItem,
      canUndo = true,
    }) {
      // Create undo item for delete
      const deleteItem = undoItem || new DocumentChangeStackItem(sharedDoc);
      const isStandaloneTransaction = !undoItem;

      // Get the page or modal id depending on the current canvas.
      const pageOrModalId = canvasName === 'modalModules'
        ? this.currentModalCanvasId
        : this.currentPageId;

      // Create a function to get the canvas we're adding to.
      const getCanvas = partial(utils.findCanvasByPageOrModalId, canvasName, pageOrModalId);
      const yCanvas = getCanvas(sharedDoc.document);
      const yModule = utils.findYModuleById(canvasName, sharedDoc.document, id);

      if (yCanvas && yModule) {
        const deleteModule = yModule.toJSON();

        // Create a change to delete the module that is undoable.
        deleteItem.add(DocumentChange.delete(getCanvas, deleteModule));

        // Clear the copy source_id from the modal
        if (canvasName === 'modalModules') {
          this[types.CLEAR_MODAL_CANVAS_SOURCE_ID]({ undoItem: deleteItem });
        }

        if (isStandaloneTransaction) {
          const transactionOrigin = canUndo
            ? LOCAL_TRANSACTION
            : LOCAL_TRANSACTION_NOT_UNDOABLE;

          // Set tags on the undo item.
          deleteItem.tags.set('pageOrModalId', yCanvas.parent?.get('id'));
          deleteItem.tags.set('panel', { name: canvasName });

          // Apply the changes to the yDoc.
          deleteItem.apply(transactionOrigin);

          // If we are allowing undo, then add to undo manager.
          if (canUndo) {
            applyUndoable(deleteItem);
          }
        }
      }
    },
    [types.UPDATE_DRAFT_NAME](draftName) {
      sharedDoc.change((yDoc) => {
        yDoc.get('draft').set('name', draftName);
      }, LOCAL_TRANSACTION);
    },
    [types.UPDATE_DRAFT_DESCRIPTION](description) {
      sharedDoc.change((yDoc) => {
        yDoc.get('draft').get('options').set('description', description);
      }, LOCAL_TRANSACTION);
    },
    [types.DELETE_SECTION](section) {
      // Create an undo item for the section delete.
      const deleteSectionItem = new DocumentChangeStackItem(sharedDoc);
      deleteSectionItem.tags.set('panel', { name: 'pageDrawer' });

      // Add changes to remove pages from section.
      const yPages = utils.getPages(sharedDoc.document);
      yPages.forEach((yPage) => {
        if (yPage.get('options').get('group_id') === section.id) {
          deleteSectionItem.add(DocumentChange.update(
            utils.findYPageOptionsById,
            { group_id: undefined },
            yPage.get('id'),
          ));
        }
      });

      // Add change to delete the section.
      deleteSectionItem.add(DocumentChange.delete(utils.getSections, section));

      // Apply changes and add to undo manager.
      applyUndoable(deleteSectionItem);
    },
    [types.ADD_SECTION]({ addedSection, pageIds }) {
      // Create undo item.
      const addSectionItem = new DocumentChangeStackItem(sharedDoc);
      addSectionItem.tags.set('panel', { name: 'pageDrawer' });

      // Add change to add the section.
      addSectionItem.add(DocumentChange.add(utils.getSections, addedSection));

      // Add changes to add specified pages to the new section.
      pageIds.forEach((pageId) => {
        addSectionItem.add(DocumentChange.update(
          utils.findYPageOptionsById,
          { group_id: addedSection.id },
          pageId,
        ));
      });

      // Apply changes and add to undo manager.
      applyUndoable(addSectionItem);
    },
    [types.UPDATE_SECTION](section) {
      // Create undo item.
      const updateSectionItem = new DocumentChangeStackItem(sharedDoc);
      updateSectionItem.tags.set('panel', { name: 'pageDrawer' });

      // Add change to update the section.
      updateSectionItem.add(DocumentChange.update(
        utils.getSectionById,
        section,
      ));

      // Apply changes and add to undo manager.
      applyUndoable(updateSectionItem);
    },
    [types.UPDATE_PAGE_OPTIONS]({ options, pageId }) {
      if (!sharedDoc) return;
      // If we've passed in a page id, update that page. Otherwise update
      // the current page
      const pageIdToChange = pageId || get(this, 'currentPage.id');

      sharedDoc.change((yDoc) => {
        const yPage = yjsHelper.find(yDoc.get('draft').get('pages'),
          (page) => page.get('id') === pageIdToChange);
        if (yPage) {
          yPage.set('options', yjsHelper.toY(options));
        }
      }, LOCAL_TRANSACTION);
    },
    [types.UPDATE_ALL_PAGE_OPTIONS]({ options }) {
      sharedDoc.change((yDoc) => {
        yDoc.get('draft').get('pages').forEach((yPage) => {
          const yOptions = yPage.get('options');
          const existingOptions = yOptions ? yOptions.toJSON() : {};
          yPage.set('options', yjsHelper.toY(merge(existingOptions, options)));
        });
      }, LOCAL_TRANSACTION);
    },
    async [types.COPY_PAGES]({
      pages,
      modals,
      undoItem,
    }) {
      if (!pages.length) return null;

      // Create undo item to hold changes.
      const addPagesItem = undoItem || new DocumentChangeStackItem(sharedDoc);
      const isStandaloneTransaction = !undoItem;

      /*
        Make copies of modals used in the pages we're copying
        and add them to the draft.
      */
      const modalsToCopy = modals || getModalsForPages(pages);
      const { modalCopyMap, modalCopies } = this[types.COPY_MODAL_CANVASES]({
        modals: modalsToCopy,
        undoItem: addPagesItem,
      });

      // Copy the pages and update links to modals.
      const copiedPages = copyPages({ pages, modalCopyMap });

      copiedPages.forEach((copiedPage) => {
        addPagesItem.add(DocumentChange.add(utils.getPages, copiedPage));
      });

      if (isStandaloneTransaction) {
        // Set the panel to show when undoing or redoing this change.
        addPagesItem.tags.set('panel', { name: 'pageDrawer' });

        // Apply changes and add to undo manager.
        applyUndoable(addPagesItem);

        // View the first copied page
        await this[types.VIEW_PAGE_BY_ID](first(copiedPages).id);
      }

      return {
        pageCopies: copiedPages,
        modalCopies,
      };
    },
    [types.ADD_SLOTTED_PAGE](studioPage = {}, vnode) {
      const newPage = merge(utils.newPageTemplate(), studioPage);
      newPage.slotPage = { vnode };

      const hasSortIndex = isNumber(studioPage.sort_index);
      const minSortIndex = Math.min(...map(this.draft.pages, (p) => p.sort_index));
      newPage.sort_index = hasSortIndex ? newPage.sort_index : Number.MAX_VALUE;

      this.draft.pages.push(newPage);

      if (newPage.sort_index < minSortIndex) {
        this[types.VIEW_PAGE_BY_ID](newPage.id);
      }
    },
    [types.ADD_PAGE](payload = {}) {
      const pages = this.pagesSorted;

      // start calculating the sort index for the new page
      let sortIndex = this.currentPage.sort_index;

      // at the end?
      if (this.currentPageIndex === findLastIndex(pages)) {
        sortIndex += 1;
      } else {
        // otherwise, calc the diff between the selected and its right sibling
        sortIndex = (sortIndex + pages[this.currentPageIndex + 1].sort_index) / 2;
      }

      const { page } = payload;
      if (page) {
        // make sure the page has a default height
        if (!page.options) {
          page.options = {};
        }
        if (!page.options.height) {
          page.options.height = EDITOR_CANVAS_HEIGHT;
        }

        if (!page.options.grid_disabled) {
          // set the flag, since now all pages have grid disabled
          page.options.grid_disabled = true;
        }
      }

      // Merge the default page template with any overrides
      const newPage = merge(
        utils.newPageTemplate(this.draft.options.type),
        omit(cloneDeep(page), ['id']),
        { sort_index: sortIndex },
      );

      // New pages should use the same theme as the first page in the board
      const firstPageTheme = get(this, 'draft.pages[0].options.theme');
      newPage.options = {
        ...newPage.options, // this sidesteps the issue of newPage.options sometimes being null
        group_id: get(this.currentPage, 'options.group_id'),
        theme: payload.keepTheme
          ? newPage.options.theme
          : {
            name: 'default',
            variation: 1,
            ...firstPageTheme,
            background_image: undefined,
          },
      };

      // Make sure we don't carry over old module ids
      newPage.modules = newPage.modules.map((mod) => ({
        ...mod,
        id: uuid(),
      }));
      newPage.notes = (newPage.notes || []).map((mod) => ({
        ...mod,
        id: uuid(),
      }));

      // Create add page undo item
      const addPageItem = new DocumentChangeStackItem(sharedDoc);
      addPageItem.tags.set('panel', { name: 'pageDrawer' });
      addPageItem.add(DocumentChange.add(utils.getPages, newPage));

      // Apply changes and add to undo manager.
      applyUndoable(addPageItem);

      // View our new page
      this[types.VIEW_PAGE_BY_ID](newPage.id);

      return newPage;
    },
    [types.DELETE_PAGES]({ pageIds }) {
      // Create undo item for page deletes.
      const deletePagesItem = new DocumentChangeStackItem(sharedDoc);
      deletePagesItem.tags.set('panel', { name: 'pageDrawer' });

      // Add delete page changes to undo item.
      pageIds
        .map((pageId) => this.pagesById[pageId])
        .filter((page) => page)
        .forEach((page) => {
          deletePagesItem.add(DocumentChange.delete(utils.getPages, page));
        });

      /*
        Apply the delete page changes to the yDoc before we query below to find
        empty sections that need to be removed.
      */
      deletePagesItem.apply();

      // Get sections that have no pages.
      const emptySections = utils.getEmptySections(sharedDoc.document);

      /*
        If there are no empty sections to delete, only add the deleted pages
        to the undo history.
      */
      if (!emptySections.length) {
        applyUndoable(deletePagesItem);
        return;
      }

      // Otherwise, add changes to delete empty sections.
      emptySections.forEach((section) => {
        deletePagesItem.add(DocumentChange.delete(utils.getSections, section));
      });

      // Apply changes and add to undo manager.
      applyUndoable(deletePagesItem);
    },
    [types.UPDATE_PAGE](payload) {
      sharedDoc.change((yDoc) => {
        const yPage = utils.findYPageById(yDoc, payload.id);
        if (yPage) {
          yjsHelper.updateYMap(payload, yPage);
        }
      }, LOCAL_TRANSACTION);
    },
    [types.UPDATE_PAGES](pages) {
      // Create undo item.
      const updatePagesItem = new DocumentChangeStackItem(sharedDoc);
      updatePagesItem.tags.set('panel', { name: 'pageDrawer' });

      // Add changes to update the pages.
      pages.forEach((page) => {
        updatePagesItem.add(DocumentChange.update(
          utils.findYPageById,
          page,
        ));
      });

      // Apply changes and add to undo manager.
      applyUndoable(updatePagesItem);
    },
    [types.UPDATE_PAGES_OPTIONS]({
      pages,
      undoItem,
    }) {
      // Create undo item.
      const updateOptionsItem = undoItem || new DocumentChangeStackItem(sharedDoc);
      const isStandaloneTransaction = !undoItem;

      // Add changes to update the page options.
      pages.forEach((page) => {
        const yPage = utils.findYPageById(sharedDoc.document, page.id);

        if (yPage) {
          if (yPage.get('options')) {
            // If the page has an options object, then update it.
            updateOptionsItem.add(DocumentChange.update(
              utils.findYPageOptionsById,
              page.options,
              page.id,
            ));
          } else {
            // Otherwise create a new options object on the page.
            updateOptionsItem.add(DocumentChange.update(
              utils.findYPageById,
              { options: cloneDeep(page.options) },
              page.id,
            ));
          }
        }
      });

      if (isStandaloneTransaction) {
        // Set panel to show when undoing or redoing this item.
        updateOptionsItem.tags.set('panel', { name: 'pageDrawer' });

        // Apply changes and add to undo manager.
        applyUndoable(updateOptionsItem);
      }
    },
    [types.UPDATE_PAGE_HEIGHT](height) {
      if (!height) return;

      // Create undo item.
      const undoItem = new DocumentChangeStackItem(sharedDoc);

      // Update the page height in the page's options.
      this[types.UPDATE_PAGES_OPTIONS]({
        pages: [{
          id: this.currentPageId,
          options: { height },
        }],
        undoItem,
      });

      // Set panel and page to show when undoing or redoing this item.
      undoItem.tags.set('panel', { name: 'modules' });
      undoItem.tags.set('pageOrModalId', this.currentPageId);

      // Apply changes and add to undo manager.
      applyUndoable(undoItem);
    },
    [types.MOVE_PAGES]({ pages }) {
      // Create undo item for the page moves.
      const movePagesItem = new DocumentChangeStackItem(sharedDoc);
      movePagesItem.tags.set('panel', { name: 'pageDrawer' });

      // Add changes to update the pages.
      pages.forEach((page) => {
        // Change to update the page's sort order.
        if (isNumber(page.sort_index)) {
          movePagesItem.add(DocumentChange.update(
            utils.findYPageById,
            { sort_index: page.sort_index },
            page.id,
          ));
        }

        // Change to update the page's section.
        movePagesItem.add(DocumentChange.update(
          utils.findYPageOptionsById,
          { group_id: page.options?.group_id },
          page.id,
        ));
      });

      /*
        Apply the page updates to the yDoc first before querying below to find
        the sections that are now empty.
      */
      movePagesItem.apply();

      // Get sections that are empty after moving the pages.
      const emptySections = utils.getEmptySections(sharedDoc.document);

      /*
        If there are no empty sections to delete, only add the moved pages
        to the undo history.
      */
      if (!emptySections.length) {
        applyUndoable(movePagesItem);
        return;
      }

      // Otherwise, delete empty sections.
      emptySections.forEach((section) => {
        movePagesItem.add(DocumentChange.delete(utils.getSections, section));
      });

      // Apply changes and add to undo manager.
      applyUndoable(movePagesItem);
    },
    [types.UPDATE_CHAT_ENABLED](chatEnabled) {
      sharedDoc.change((doc) => {
        doc.get('draft').get('options').set('chat_enabled', chatEnabled);
      }, LOCAL_TRANSACTION);
    },
    /**
     * publish a draft
     * payload must contain draftId param
     */
    async [types.PUBLISH_DRAFT]({ draftId }) {
      const appStore = useAppStore();
      // grab the id
      const creation = {
        id: draftId,
      };

      // set loading
      appStore[types.UPDATE_LOADING](true);

      const result = await this.titanApiFetch('/creations/', {
        method: 'POST',
        body: creation,
        headers: {
          'X-Token': appStore.apiToken,
        },
      });

      // Update the store with the new draft
      if (result.status === 201) {
        this.publishedCreationId = result.data.creation.id;
        appStore[types.UPDATE_LOADING](false);

        const publishedOn = get(result, 'data.creation.metadata.updated_on');
        if (publishedOn && sharedDoc) {
          sharedDoc.change((yDoc) => {
            yDoc.get('draft').get('metadata').set('published_on', publishedOn);
          }, LOCAL_TRANSACTION);
        }
      } else {
        appStore[types.UPDATE_LOADING](false);
        // Error handling
        throw result;
      }

      return get(result, 'data.creation');
    },
    [types.SET_DRAFT_IS_DIRTY](isDirty) {
      if (!sharedDoc) return;
      sharedDoc.change((yDoc) => {
        yDoc.get('session').set('draftIsDirty', isDirty);
      }, LOCAL_TRANSACTION);
    },
    [types.COMMIT_UPDATE_PAGE](payload) {
      const idx = this.draft.pages.findIndex((page) => page.id === payload.id);
      if (idx === -1) return; // prevent editing of pages that don't exist
      this.draft.pages[idx] = payload;
    },
    [types.APPLY_UPDATE_PAGE]({ change, transaction }) {
      const assetStore = useAssetStore();
      // Get the page
      const newPage = utils.isUpdatePageOptions(change)
        ? change.target.parent.toJSON()
        : change.target.toJSON();
      const existingPage = this.draft.pages.find((page) => page.id === newPage.id);

      // Update state
      this[types.COMMIT_UPDATE_PAGE](newPage);

      const newBackgroundImage = get(newPage, 'options.theme.background_image');
      const existingBackgroundImage = get(existingPage, 'options.theme.background_image');
      const newPageAssetId = get(newPage, 'options.asset_id');
      const existingPageAssetId = get(existingPage, 'options.asset_id');

      // Update the asset cache if the background image id has changed
      if (newBackgroundImage && existingBackgroundImage !== newBackgroundImage) {
        assetStore[types.CACHE_ASSET](newBackgroundImage);
      }

      // Update the asset cache if the page asset_id has changed
      if (newPageAssetId && existingPageAssetId !== newPageAssetId) {
        assetStore[types.CACHE_ASSET](newPageAssetId);
      }

      if (sharedDoc.isHost) {
        this[types.ENQUEUE_PAGE_PATCH]({ page: newPage, action: 'update' });
      }

      /*
        If the page was updated by another user, then:
        1. Remove any updates to the same properties from this user's
        undo history for this page. This is to prevent this user from
        undoing another user's more recent change to a property value.
        2. Remove any add or delete undo items for this page from
        this user's undo history.
      */
      if (!transactionIsLocal(transaction)) {
        const updatedKeys = [...change.changes?.keys?.entries()];
        this[types.REMOVE_FROM_UNDO_HISTORY]((item) => {
          const tags = item.tags.get(newPage.id);
          if (!tags) return false;
          return tags.has('add')
            || tags.has('delete')
            || updatedKeys.some((key) => tags.has('update') && tags.has(first(key)));
        });
      }
    },
    [types.CHECK_MODULE_Z_POSITIONS](changes) {
      // Extract all the pages that were updated with these changes.
      const pageIds = changes.reduce((pageIdSet, change) => {
        if (utils.isUpdateModule(change) && change.path[2] === 'modules') {
          const updatedPageId = change.target.parent.parent.get('id');
          if (updatedPageId) pageIdSet.add(updatedPageId);
        } else if (utils.isAddModule(change) && change.path[2] === 'modules') {
          const pageId = change.target.parent.get('id');
          if (pageId) pageIdSet.add(pageId);
        } else if (utils.isAddPage(change)) {
          chain([...change.changes.added.values()])
            .map((added) => added.content.type.get('id'))
            .uniq()
            .value()
            .forEach((newPageId) => {
              pageIdSet.add(newPageId);
            });
        } else if (utils.isUpdatePage(change)) {
          const updatedPageId = change.target.get('id');
          if (updatedPageId) pageIdSet.add(updatedPageId);
        }
        return pageIdSet;
      }, new Set());

      if (!pageIds.size) {
        return;
      }

      sharedDoc.change((yDoc) => {
        const yPagesById = {};
        yDoc.get('draft').get('pages').forEach((yPage) => {
          yPagesById[yPage.get('id')] = yPage;
        });

        pageIds.forEach((pageId) => {
          const page = yPagesById[pageId];
          if (!page) return;
          const gridDisabled = page.get('options') && page.get('options').get('grid_disabled');
          if (!gridDisabled) return;
          const modules = page.get('modules');
          if (!modules) return;
          const yModulesById = {};
          modules.forEach((yModule) => {
            yModulesById[yModule.get('id')] = yModule;
          });

          const modulesSorted = this.getModulesByPageIdOrderedByZPosition(pageId);
          const maxModIndex = modulesSorted.length - 1;

          // Loop through the modules and look for consecutive modules with the same z_position.
          for (let modIndex = 0; modIndex <= maxModIndex;) {
            const currentZPosition = modulesSorted[modIndex].layout.z_position;
            let nextModIndex = modIndex + 1;

            const getNextZPosition = () => (nextModIndex <= maxModIndex
              ? modulesSorted[nextModIndex].layout.z_position
              : modulesSorted[maxModIndex].layout.z_position + 1);
            let nextZPosition = getNextZPosition();

            while (nextZPosition === currentZPosition) {
              nextModIndex += 1;
              nextZPosition = getNextZPosition();
            }

            const modulesWithSameZPosition = modulesSorted.slice(modIndex, nextModIndex);

            /*
              If modules with the same z_position are found, calculate unique z_positions for each
              while maintaining z_position order.
            */
            if (modulesWithSameZPosition.length > 1) {
              const previousZPosition = modIndex === 0
                ? yModulesById[modulesSorted[modIndex].id].get('layout').get('z_position') - 1
                : yModulesById[modulesSorted[modIndex - 1].id].get('layout').get('z_position');
              const fractionalIncrement = (nextZPosition - previousZPosition)
                / (modulesWithSameZPosition.length + 1);

              modulesWithSameZPosition.forEach((mod, index) => {
                const yModule = yModulesById[mod.id];
                if (yModule) {
                  const increment = (index + 1) * fractionalIncrement;
                  const layoutJson = yModule.get('layout').toJSON();
                  layoutJson.z_position = previousZPosition + increment;
                  /*
                    Update the entire layout object to ensure 'APPLY_UPDATE_MODULE' handlers
                    are called after the update.
                  */
                  yModule.set('layout', yjsHelper.toY(layoutJson));
                }
              });
            }

            modIndex = nextModIndex;
          }
        });
      }, LOCAL_TRANSACTION);
    },
    [types.CHECK_SORT_INDEXES]() {
      const { pagesSorted } = this;
      if (!pagesSorted) return;

      sharedDoc.change((yDoc) => {
        const yPagesById = {};
        yDoc.get('draft').get('pages').forEach((yPage) => {
          yPagesById[yPage.get('id')] = yPage;
        });

        const maxPageIndex = pagesSorted.length - 1;
        for (let pageIndex = 0; pageIndex <= maxPageIndex;) {
          const currentSortIndex = pagesSorted[pageIndex].sort_index;
          let nextPageIndex = pageIndex + 1;

          const getNextSortIndex = () => (nextPageIndex <= maxPageIndex
            ? pagesSorted[nextPageIndex].sort_index
            : pagesSorted[maxPageIndex].sort_index + 1);
          let nextSortIndex = getNextSortIndex();

          while (nextSortIndex === currentSortIndex) {
            nextPageIndex += 1;
            nextSortIndex = getNextSortIndex();
          }

          const pagesWithSameSortIndex = pagesSorted.slice(pageIndex, nextPageIndex);

          if (pagesWithSameSortIndex.length > 1) {
            const previousSortIndex = pageIndex === 0
              ? pagesSorted[pageIndex].sort_index - 1
              : yPagesById[pagesSorted[pageIndex - 1].id].get('sort_index');
            const fractionalIncrement = (nextSortIndex - previousSortIndex)
              / (pagesWithSameSortIndex.length + 1);

            pagesWithSameSortIndex.forEach((page, index) => {
              const yPage = yPagesById[page.id];
              if (yPage) {
                const increment = (index + 1) * fractionalIncrement;
                yPage.set('sort_index', previousSortIndex + increment);
              }
            });
          }

          pageIndex = nextPageIndex;
        }
      }, LOCAL_TRANSACTION);
    },
    [types.COMMIT_DELETE_PAGE](pageId) {
      if (this.draft.pages.length === 1) return; // prevent deleting the only page

      const idx = this.draft.pages.findIndex((page) => page.id === pageId);
      if (idx === -1) return; // prevent deletion of pages that don't exist

      // Get the page's position in the array or sorted pages.
      const deletedPageSortedIndex = this.pagesSorted
        .findIndex((page) => page.id === pageId);

      this.draft.pages.splice(idx, 1);

      // If the current page was deleted, reset to closest page.
      if (this.currentPageId === pageId) {
        const maxPagesSortedIndex = this.pagesSorted.length - 1;
        const currentPageId = deletedPageSortedIndex > maxPagesSortedIndex
          ? this.pagesSorted[maxPagesSortedIndex].id
          : this.pagesSorted[deletedPageSortedIndex].id;
        this.currentPageId = currentPageId;
      }
    },
    [types.APPLY_DELETE_PAGE]({ change, transaction }) {
      // get the page
      chain([...change.changes.deleted.values()])
        // eslint-disable-next-line no-underscore-dangle
        .map((deleted) => deleted.content.type._map.get('id').content.arr[0])
        .uniq()
        .value()
        .forEach((pageId) => {
          // Update state
          this[types.COMMIT_DELETE_PAGE](pageId);

          if (sharedDoc.isHost) {
            this[types.ENQUEUE_PAGE_PATCH]({ page: { id: pageId }, action: 'remove' });
          }

          /*
            If the page was deleted by another user, then remove any undo items related
            to this page from this user's undo history.
          */
          if (!transactionIsLocal(transaction)) {
            // Remove history for updates to this page.
            this[types.REMOVE_FROM_UNDO_HISTORY]((item) => (
              item.tags.get(pageId) || item.tags.get('pageOrModalId') === pageId
            ));
          }
        });
    },
    [types.APPLY_ADD_PAGE]({ change, yDoc }) {
      const assetStore = useAssetStore();
      // get the page
      chain([...change.changes.added.values()])
        .map((added) => added.content.type.get('id'))
        .uniq()
        .value()
        .forEach((newPageId) => {
          const pages = yDoc.get('draft').get('pages');
          const page = yjsHelper.find(pages, (yPage) => yPage.get('id') === newPageId);
          if (page) {
            const newPage = page.toJSON();

            // Update state
            this.draft.pages.push(newPage);

            // Update the asset cache if the new slide is a full-slide asset
            // eslint-disable-next-line
            if (newPage.options?.asset_id) {
              assetStore[types.CACHE_ASSET](newPage.options.asset_id);
            }

            if (sharedDoc.isHost) {
              this[types.ENQUEUE_PAGE_PATCH]({ page: newPage, action: 'add' });
            }
          }
        });
    },
    [types.COMMIT_UPDATE_MODULE](payload) {
      const {
        pageIndex,
        canvasName,
        moduleIndex,
        newModule,
      } = payload;

      this.draft.pages[pageIndex][canvasName][moduleIndex] = newModule;
    },
    [types.APPLY_UPDATE_MODULE]({ change, transaction }) {
      const assetStore = useAssetStore();
      const trackingStore = useTrackingStore();
      const uploadStore = useUploadStore();
      const updatedModule = change.target.toJSON();

      // find the page by module id
      const pageIndex = utils.getPageIndexByModuleId(this.draft, updatedModule.id);
      if (pageIndex === -1) return;
      const canvasName = change.path[2];
      const page = this.draft.pages[pageIndex];
      const moduleIndex = page[canvasName]
        .findIndex((mod) => mod.id === updatedModule.id);
      if (moduleIndex === -1) return;

      const existingModule = page[canvasName][moduleIndex];

      // If we've swapped assets, update the asset cache
      if (['asset', 'upload'].includes(existingModule.type)
        && existingModule.options.asset_id !== updatedModule.options.asset_id) {
        assetStore[types.CACHE_ASSET](updatedModule.options.asset_id);
        if (transaction.origin === LOCAL_TRANSACTION) {
          trackingStore[types.SEND_CLICKLOG_EVENT]({
            ids: [updatedModule.options.asset_id],
          });
        }
      } else if (existingModule.type === 'upload' && existingModule.options.upload_id !== updatedModule.options.upload_id) {
        uploadStore[types.CACHE_UPLOAD](updatedModule.options.upload_id);
      }

      const moduleResized = existingModule.layout.width !== updatedModule.layout.width
        || existingModule.layout.height !== updatedModule.layout.height;
      const moduleMoved = existingModule.layout.x_position !== updatedModule.layout.x_position
        || existingModule.layout.y_position !== updatedModule.layout.y_position;

      // Update state
      this[types.COMMIT_UPDATE_MODULE]({
        canvasName,
        moduleIndex,
        newModule: updatedModule,
        pageIndex,
      });

      if (moduleResized) bus.emit('resized', updatedModule.id);
      if (moduleMoved) bus.emit('moved', updatedModule.id);

      if (sharedDoc.isHost) {
        this[types.ENQUEUE_PAGE_PATCH]({ page, action: 'update' });
      }

      /*
        If this module update came from another connected user, then remove all undo
        history for this module for the current user.
      */
      if (!transactionIsLocal(transaction)) {
        this[types.REMOVE_FROM_UNDO_HISTORY]((item) => item.tags.get(updatedModule.id));
      }

      // Broadcast the updated module
      nextTick(() => {
        bus.emit('module:updated', {
          updatedModule,
          isLocalUndoRedo: transaction.origin === LOCAL_TRANSACTION_UNDO_MANAGER,
          isLocal: transactionIsLocal(transaction),
          isLocalNotUndoable: transaction.origin === LOCAL_TRANSACTION_NOT_UNDOABLE,
        });
      });
    },
    [types.COMMIT_UPDATE_MODAL_MODULE](payload) {
      const {
        modalIndex,
        moduleIndex,
        newModule,
      } = payload;

      this.draft.modals[modalIndex].modules[moduleIndex] = newModule;
    },
    [types.APPLY_UPDATE_MODAL_MODULE]({ change, transaction }) {
      const assetStore = useAssetStore();
      const trackingStore = useTrackingStore();
      const uploadStore = useUploadStore();
      const updatedModule = change.target.toJSON();

      // find the page by module id
      const modalIndex = utils.getModalIndexByModuleId(this.draft, updatedModule.id);
      if (modalIndex === -1) return;
      const modal = this.draft.modals[modalIndex];
      const moduleIndex = modal.modules
        .findIndex((mod) => mod.id === updatedModule.id);
      if (moduleIndex === -1) return;

      const existingModule = modal.modules[moduleIndex];

      // If we've swapped assets, update the asset cache
      if (['asset', 'upload'].includes(existingModule.type)
        && existingModule.options.asset_id !== updatedModule.options.asset_id) {
        assetStore[types.CACHE_ASSET](updatedModule.options.asset_id);
        if (transaction.origin === LOCAL_TRANSACTION) {
          trackingStore[types.SEND_CLICKLOG_EVENT]({
            ids: [updatedModule.options.asset_id],
          });
        }
      } else if (existingModule.type === 'upload' && existingModule.options.upload_id !== updatedModule.options.upload_id) {
        uploadStore[types.CACHE_UPLOAD](updatedModule.options.upload_id);
      }

      const moduleResized = existingModule.layout.width !== updatedModule.layout.width
        || existingModule.layout.height !== updatedModule.layout.height;
      const moduleMoved = existingModule.layout.x_position !== updatedModule.layout.x_position
        || existingModule.layout.y_position !== updatedModule.layout.y_position;

      // Update state
      this[types.COMMIT_UPDATE_MODAL_MODULE]({
        moduleIndex,
        newModule: updatedModule,
        modalIndex,
      });

      if (moduleResized) bus.emit('resized', updatedModule.id);
      if (moduleMoved) bus.emit('moved', updatedModule.id);

      if (sharedDoc.isHost) {
        this[types.ENQUEUE_MODAL_PATCH]({ modal, action: 'update' });
      }

      /*
        If this module update came from another connected user, then remove all undo
        history for this module for the current user.
      */
      if (!transactionIsLocal(transaction)) {
        this[types.REMOVE_FROM_UNDO_HISTORY]((item) => item.tags.get(updatedModule.id));
      }

      // Broadcast the updated module.
      nextTick(() => {
        bus.emit('module:updated', {
          updatedModule,
          isLocalUndoRedo: transaction.origin === LOCAL_TRANSACTION_UNDO_MANAGER,
          isLocal: transactionIsLocal(transaction),
          isLocalNotUndoable: transaction.origin === LOCAL_TRANSACTION_NOT_UNDOABLE,
        });
      });
    },
    [types.COMMIT_ADD_MODULE](payload) {
      payload.page[payload.canvasName].push(payload.newModule);
    },
    [types.APPLY_ADD_MODULE]({ change, yDoc, transaction }) {
      const assetStore = useAssetStore();
      const trackingStore = useTrackingStore();
      const uploadStore = useUploadStore();
      const pageId = change.target.parent.get('id');
      const yDocPageIndex = yjsHelper.findIndex(yDoc.get('draft').get('pages'),
        (page) => page.get('id') === pageId);
      if (yDocPageIndex === -1) return;
      const canvasName = change.path[2];
      const yModules = yDoc.get('draft').get('pages').get(yDocPageIndex).get(canvasName);

      chain([...change.changes.added.entries()])
        .map((added) => map(added, (item) => item.content.type.get('id')))
        .flatten()
        .uniq()
        .value()
        .forEach((newModuleId) => {
          const yNewModule = yjsHelper.find(yModules, (yMod) => yMod.get('id') === newModuleId);
          if (yNewModule) {
            const newModule = yNewModule.toJSON();

            const pageToUpdate = this.draft.pages.find((page) => page.id === pageId);
            if (!pageToUpdate) return;

            const moduleExists = pageToUpdate[canvasName].find((mod) => mod.id === newModule.id);
            if (moduleExists) return;

            this[types.COMMIT_ADD_MODULE]({ page: pageToUpdate, newModule, canvasName });

            // Cache our new asset or upload if necessary
            if (['asset', 'upload'].includes(newModule.type) && newModule.options.asset_id) {
              assetStore[types.CACHE_ASSET](newModule.options.asset_id);
              if (transaction.origin === LOCAL_TRANSACTION) {
                trackingStore[types.SEND_CLICKLOG_EVENT]({
                  ids: [newModule.options.asset_id],
                });
              }
            } else if (newModule.type === 'upload' && newModule.options.upload_id) {
              uploadStore[types.CACHE_UPLOAD](newModule.options.upload_id);
            }

            // update page
            if (sharedDoc.isHost) {
              this[types.ENQUEUE_PAGE_PATCH]({ page: pageToUpdate, action: 'update' });
            }

            /*
              Defer this event to ensure that any modules added have been rendered
              and can listen for this event.
            */
            nextTick(() => {
              bus.emit('module:added', {
                id: newModule.id,
                canvasName,
                page: pageToUpdate,
                isLocal: transactionIsLocal(transaction),
              });
            });
          }
        });
    },
    [types.COMMIT_ADD_MODAL_MODULE](payload) {
      payload.modal.modules.push(payload.newModule);
    },
    [types.APPLY_ADD_MODAL_MODULE]({ change, yDoc, transaction }) {
      const assetStore = useAssetStore();
      const trackingStore = useTrackingStore();
      const uploadStore = useUploadStore();
      const modalId = change.target.parent.get('id');
      const yDocModalIndex = yjsHelper.findIndex(yDoc.get('draft').get('modals'),
        (modal) => modal.get('id') === modalId);
      if (yDocModalIndex === -1) return;
      const yModules = yDoc.get('draft').get('modals').get(yDocModalIndex).get('modules');

      chain([...change.changes.added.entries()])
        .map((added) => map(added, (item) => item.content.type.get('id')))
        .flatten()
        .uniq()
        .value()
        .forEach((newModuleId) => {
          const yNewModule = yjsHelper.find(yModules, (yMod) => yMod.get('id') === newModuleId);
          if (yNewModule) {
            const newModule = yNewModule.toJSON();

            const modalToUpdate = this.draft.modals.find((modal) => modal.id === modalId);
            if (!modalToUpdate) return;

            const moduleExists = modalToUpdate.modules.find((mod) => mod.id === newModule.id);
            if (moduleExists) return;

            this[types.COMMIT_ADD_MODAL_MODULE]({ modal: modalToUpdate, newModule });

            // Cache our new asset or upload if necessary
            if (['asset', 'upload'].includes(newModule.type) && newModule.options.asset_id) {
              assetStore[types.CACHE_ASSET](newModule.options.asset_id);
              if (transaction.origin === LOCAL_TRANSACTION) {
                trackingStore[types.SEND_CLICKLOG_EVENT]({
                  ids: [newModule.options.asset_id],
                });
              }
            } else if (newModule.type === 'upload' && newModule.options.upload_id) {
              uploadStore[types.CACHE_UPLOAD](newModule.options.upload_id);
            }

            // update modal
            if (sharedDoc.isHost) {
              this[types.ENQUEUE_MODAL_PATCH]({ modal: modalToUpdate, action: 'update' });
            }

            /*
              Defer this event to ensure that any modules added have been rendered
              and can listen for this event.
            */
            nextTick(() => {
              bus.emit('module:added', {
                id: newModule.id,
                canvasName: 'modalModules',
                modal: modalToUpdate,
                isLocal: transactionIsLocal(transaction),
              });
            });
          }
        });
    },
    [types.APPLY_DELETE_MODULE]({ change, yDoc, transaction }) {
      const pageId = change.target.parent.get('id');
      const yPage = yjsHelper.find(yDoc.get('draft').get('pages'), (page) => page.get('id') === pageId);
      if (yPage) {
        const updatedPage = yPage.toJSON();
        const pageIndex = this.draft.pages.findIndex((page) => page.id === updatedPage.id);
        if (pageIndex !== -1) {
          this[types.COMMIT_UPDATE_PAGE](updatedPage);

          if (sharedDoc.isHost) {
            this[types.ENQUEUE_PAGE_PATCH]({ page: updatedPage, action: 'update' });
          }
        }
      }

      [...change.changes.deleted.entries()].forEach((entry) => {
        const yModule = get(first(entry), 'content.type._map');
        if (yModule) {
          const moduleId = get(yModule.get('id'), 'content.arr[0]');
          if (moduleId) {
            if (transactionIsLocal(transaction)) {
              // Mark the module as not being locked in the shared document.
              this[types.UPDATE_CURRENTLY_EDITING]({ [moduleId]: null });
            }
            if (!transactionIsLocal(transaction)
              || transaction.origin === LOCAL_TRANSACTION_NOT_UNDOABLE) {
              /*
                Whenever a module is deleted by a remote user or the module was deleted
                locally but is not undoable (e.g. grid conflict resolution), then remove all
                undo history for the module for the current user.
              */
              this[types.REMOVE_FROM_UNDO_HISTORY]((item) => item.tags.get(moduleId));
            }
          }
        }
      });
    },
    [types.COMMIT_UPDATE_MODAL](payload) {
      const idx = this.draft.modals.findIndex((modal) => modal.id === payload.id);
      if (idx === -1) return; // prevent editing of modals that don't exist

      this.draft.modals[idx] = payload;
    },
    [types.APPLY_DELETE_MODAL_MODULE]({ change, yDoc, transaction }) {
      const modalId = change.target.parent.get('id');
      const yModal = yjsHelper.find(yDoc.get('draft').get('modals'), (modal) => modal.get('id') === modalId);
      if (yModal) {
        const updatedModal = yModal.toJSON();
        const modalIndex = this.draft.modals.findIndex((modal) => modal.id === updatedModal.id);
        if (modalIndex !== -1) {
          this[types.COMMIT_UPDATE_MODAL](updatedModal);

          if (sharedDoc.isHost) {
            this[types.ENQUEUE_MODAL_PATCH]({ modal: updatedModal, action: 'update' });
          }
        }
      }

      [...change.changes.deleted.entries()].forEach((entry) => {
        const yModule = get(first(entry), 'content.type._map');
        if (yModule) {
          const moduleId = get(yModule.get('id'), 'content.arr[0]');
          if (moduleId) {
            if (transactionIsLocal(transaction)) {
              // Mark the module as not being locked in the shared document.
              this[types.UPDATE_CURRENTLY_EDITING]({ [moduleId]: null });
            }
            if (!transactionIsLocal(transaction)
              || transaction.origin === LOCAL_TRANSACTION_NOT_UNDOABLE) {
              /*
                Whenever a module is deleted by a remote user or the module was deleted
                locally but is not undoable (e.g. grid conflict resolution), then remove all
                undo history for the module for the current user.
              */
              this[types.REMOVE_FROM_UNDO_HISTORY]((item) => item.tags.get(moduleId));
            }
          }
        }
      });
    },
    [types.APPLY_UPDATE_DRAFT]({ change, yDoc }) {
      chain([...change.changes.keys.entries()])
        .map((entry) => first(entry))
        .uniq()
        .value()
        .forEach((property) => {
          if (property === 'name') {
            const draftName = yDoc.get('draft').get('name');
            this.draft.name = draftName;
            if (sharedDoc.isHost) {
              queue.add({
                op: 'replace',
                path: '/name',
                value: draftName,
              });
            }
          } else if (property === 'shares') {
            const yShares = yDoc.get('draft').get('shares');
            if (yShares) {
              this.draft.shares = yShares.toJSON();
            }
          }
        });
    },
    [types.UPDATE_SHARES](shares) {
      if (get(sharedDoc, 'isConnected')) {
        sharedDoc.change((yDoc) => {
          yDoc.get('draft').set('shares', yjsHelper.toY(shares));
        }, LOCAL_TRANSACTION);
      } else {
        this.draft.shares = shares;
      }
    },
    [types.UPDATE_SHARE_ID](shareId) {
      if (get(sharedDoc, 'isConnected')) {
        sharedDoc.change((yDoc) => {
          yDoc.get('draft').set('share_id', shareId);
        }, LOCAL_TRANSACTION);
      } else {
        this.draft.share_id = shareId;
      }
    },
    [types.UPDATE_SAVING](saving) {
      // This is only its own method so that it's easier to watch in tests
      this.saving = saving;
    },
    [types.APPLY_UPDATE_DRAFT_OPTIONS]({ change, yDoc }) {
      const chatStore = useChatStore();

      chain([...change.changes.keys.entries()])
        .map((entry) => first(entry))
        .uniq()
        .value()
        .forEach((property) => {
          if (['description', 'chat_enabled'].includes(property)) {
            const propertyValue = yDoc.get('draft').get('options').get(property);
            if (property === 'description') {
              this.draft.options.description = propertyValue;
              if (sharedDoc.isHost) {
                throttlePatchDraftDescription({
                  op: 'add',
                  path: `/options/${property}`,
                  value: propertyValue,
                });
              }
            } else if (property === 'chat_enabled') {
              this.draft.options.chat_enabled = propertyValue;
              if (sharedDoc.isHost) {
                queue.add({
                  op: 'add',
                  path: `/options/${property}`,
                  value: propertyValue,
                });
              }

              // Ensure chat module toggle is kept in sync.
              chatStore[types.SET_CHAT_TOGGLE_ENABLED](propertyValue);
              if (propertyValue) {
                // If the chat is being turned on, we have no shared document or
                // session and need to initialize one
                chatStore[types.GET_CHAT]({ draft: this.draft });
              } else {
                // If the chat is being turned off, we can clear out the chat session
                chatStore[types.CLOSE_EDITING_SESSION](null);
              }
            }
          }
        });
    },
    [types.COMMIT_ADD_SECTION](payload) {
      if (!this.draft?.options?.groups) return;
      this.draft.options.groups.push(payload);
    },
    [types.COMMIT_DELETE_SECTION](sectionId) {
      if (!this.draft?.options?.groups) return;
      const sectionIdx = this.draft.options.groups
        .findIndex((section) => section.id === sectionId);
      if (sectionIdx === -1) return;
      this.draft.options.groups.splice(sectionIdx, 1);
    },
    [types.COMMIT_UPDATE_SECTION](payload) {
      if (!this.draft?.options?.groups) return;
      const sectionIdx = this.draft.options.groups
        .findIndex((section) => section.id === payload.id);
      if (sectionIdx === -1) return;
      this.draft.options.groups[sectionIdx] = payload;
    },
    [types.APPLY_UPDATE_SECTION]({ change, yDoc, transaction }) {
      // Apply added sections
      chain([...change.changes.added.values()])
        .map((added) => added.content.type.get('id'))
        .uniq()
        .value()
        .forEach((sectionId) => {
          const ySections = utils.getSections(yDoc);
          const section = yjsHelper.find(ySections, (ySection) => ySection.get('id') === sectionId);

          if (section) {
            const newSection = section.toJSON();

            this[types.COMMIT_ADD_SECTION](newSection);
            if (sharedDoc.isHost) {
              queue.add({
                op: 'add',
                path: '/options/groups/-',
                value: newSection,
              });
            }
          }
        });

      // Apply deleted sections
      chain([...change.changes.deleted.values()])
        // eslint-disable-next-line no-underscore-dangle
        .map((deleted) => deleted.content.type._map.get('id').content.arr[0])
        .uniq()
        .value()
        .forEach((sectionId) => {
          if (sharedDoc.isHost) {
            const sectionIdx = this.draft.options.groups
              .findIndex((section) => section.id === sectionId);

            if (sectionIdx === -1) return;

            queue.add({
              op: 'remove',
              path: `/options/groups/${sectionIdx}`,
            });
          }
          this[types.COMMIT_DELETE_SECTION](sectionId);

          /*
            If the section was deleted by another user, then remove any undo items related
            to this section from this user's undo history.
          */
          if (!transactionIsLocal(transaction)) {
            this[types.REMOVE_FROM_UNDO_HISTORY]((item) => item.tags.get(sectionId));
          }
        });

      // Apply updated sections
      const updatedKeys = [...change.changes.keys.entries()];
      if (updatedKeys.length) {
        const sectionIdx = last(change.path);
        const ySection = utils.getSections(yDoc).get(sectionIdx);
        if (!ySection) return;

        const newSection = ySection.toJSON();

        if (sharedDoc.isHost) {
          queue.add({
            op: 'replace',
            path: `/options/groups/${newSection.id}`,
            value: newSection,
          });
        }
        this[types.COMMIT_UPDATE_SECTION](newSection);

        /*
          If the section was updated by another user, then:
          1. Remove any updates to the same properties from this user's
          undo history for this section. This is to prevent this user from
          undoing another user's more recent change to a property value.
          2. Remove any add or delete undo items for this section from
          this user's undo history.
        */
        if (!transactionIsLocal(transaction)) {
          this[types.REMOVE_FROM_UNDO_HISTORY]((item) => {
            const tags = item.tags.get(newSection.id);
            if (!tags) return false;
            return tags.has('add')
              || tags.has('delete')
              || updatedKeys.some((key) => tags.has('update') && tags.has(first(key)));
          });
        }
      }
    },
    [types.APPLY_UPDATE_DRAFT_METADATA]({ change, yDoc }) {
      chain([...change.changes.keys.entries()])
        .map((entry) => first(entry))
        .uniq()
        .value()
        .forEach((property) => {
          if (property === 'published_on') {
            const publishedOn = yDoc.get('draft').get('metadata').get('published_on');
            this.draft.metadata.published_on = publishedOn;
          }
        });
    },
    [types.ENQUEUE_PAGE_PATCH]({ page, action }) {
      if (action === 'update') {
        queue.add({
          op: 'replace',
          path: `/pages/${page.id}`,
          value: page,
        });
      } else if (action === 'remove') {
        queue.add({
          op: 'remove',
          path: `/pages/${page.id}`,
        });
      } else if (action === 'add') {
        queue.add({
          op: 'add',
          path: '/pages/-',
          value: page,
        });
      }
    },
    [types.ENQUEUE_MODAL_PATCH]({ modal, action }) {
      if (action === 'update') {
        queue.add({
          op: 'replace',
          path: `/modals/${modal.id}`,
          value: modal,
        });
      } else if (action === 'remove') {
        queue.add({
          op: 'remove',
          path: `/modals/${modal.id}`,
        });
      } else if (action === 'add') {
        queue.add({
          op: 'add',
          path: '/modals/-',
          value: modal,
        });
      }
    },
    [types.ENQUEUE_DRAFT_PATCH](draft) {
      ['name', 'pages'].forEach((key) => {
        queue.add({
          op: 'replace',
          path: `/${key}`,
          value: draft[key],
        });
      });
      if ('description' in draft.options) {
        queue.add({
          op: 'add',
          path: '/options/description',
          value: get(draft, 'options.description'),
        });
      }
      if ('chat_enabled' in draft.options) {
        queue.add({
          op: 'add',
          path: '/options/chat_enabled',
          value: get(draft, 'options.chat_enabled'),
        });
      }
    },
    async [types.OPEN_EDITING_SESSION](payload) {
      const appStore = useAppStore();
      const errorStore = useErrorStore();
      const userStore = useUserStore();
      try {
        appStore[types.UPDATE_LOADING](true);
        this[types.CLOSE_EDITOR_DRAWERS]();
        this[types.CLOSE_EDITING_SESSION]();
        const {
          options: draftOptions,
          id,
          references,
          is_deleted: isDeleted,
          metadata: { published_on: publishedOn } = {},
        } = await this[types.GET_DRAFT_REFERENCES](payload);

        /*
          If the draft is deleted, don't open the editing session and
          throw an error instead.
        */
        if (isDeleted) {
          throw new DraftIsDeletedError(
            this.draftPointerAssetId({ references }),
            id,
            !!publishedOn,
          );
        }

        const { draftId, pageId } = payload;

        const draftPointerAssetReference = references
          .find((ref) => ref.reference_type === 'asset' && ref.reference_subtype === 'head');

        if (draftPointerAssetReference && draftId !== draftPointerAssetReference.reference_id) {
          // If we've landed on a draft using the mongo id, redirect to the same route with
          // the pointer asset id instead
          window.location.href = window.location.href.replace(
            `id=${draftId}`,
            `id=${draftPointerAssetReference.reference_id}`,
          );
          return;
        }

        sharedDoc = new SharedDocument({
          appId: 'studio',
          env: this.env,
          region: this.region,
          resourceId: id,
          references,
          baseUrl: this.domains.titanDomain,
          userData: {
            firstName: userStore.user.first_name,
            lastName: userStore.user.last_name,
          },
          getDocument: () => this[types.GET_SHARED_DOCUMENT](payload),
          userToken: appStore.apiToken,
          axiosClient: this.deApi,
        });

        sharedDoc.on('connected', (yDoc) => {
          const jsDoc = yDoc.toJSON();
          const { draft } = jsDoc;

          if (draft.options.is_template && draft.options.template_type === 'activity'
            && !userStore.isDeUser) {
            // Non-editorial users should not be able to edit activity templates
            errorStore[types.SET_ERROR]({
              active: true,
              error: {
                component: 'ActivityTemplatePermissionsError',
                options: {
                  creation: draft,
                },
              },
            });
            this[types.CLOSE_EDITING_SESSION]();
            appStore[types.UPDATE_LOADING](false);
            return;
          }

          /*
            IMPORTANT: The order of the next three statements should be preserved because
            INIT_DRAFT will modify the session so the observer must already be attached.
            1. SET_SESSION with initial value
            2. Attach observeDeep() to session
            3. INIT_DRAFT
          */
          this.session = jsDoc.session;
          yDoc.get('session').observeDeep(() => {
            this.session = yDoc.get('session').toJSON();
          });
          this[types.INIT_DRAFT]({ draft, pageId });
          this[types.START_POLL]();
          this[types.INIT_DRAFT_UNDO]();

          yDoc.get('draft').observeDeep((changes, transaction) => {
            let changeNotHandled = false;
            changes.forEach((change) => {
              if (utils.isUpdateDraft(change)) {
                this[types.APPLY_UPDATE_DRAFT]({ change, yDoc });
              } else if (utils.isUpdateDraftOptions(change)) {
                this[types.APPLY_UPDATE_DRAFT_OPTIONS]({ change, yDoc });
              } else if (utils.isUpdateSection(change)) {
                this[types.APPLY_UPDATE_SECTION]({ change, yDoc, transaction });
              } else if (utils.isUpdateDraftMetadata(change)) {
                this[types.APPLY_UPDATE_DRAFT_METADATA]({ change, yDoc });
              } else if (utils.isUpdateModule(change)) {
                this[types.APPLY_UPDATE_MODULE]({ change, transaction });
              } else if (utils.isUpdateModalModule(change)) {
                this[types.APPLY_UPDATE_MODAL_MODULE]({ change, transaction });
              } else if (utils.isAddOrDeleteModule(change)) {
                if (utils.isAddModule(change)) {
                  this[types.APPLY_ADD_MODULE]({ change, yDoc, transaction });
                }
                if (utils.isDeleteModule(change)) {
                  this[types.APPLY_DELETE_MODULE]({ change, yDoc, transaction });
                }
              } else if (utils.isAddOrDeleteModalModule(change)) {
                if (utils.isAddModalModule(change)) {
                  this[types.APPLY_ADD_MODAL_MODULE]({ change, yDoc, transaction });
                }
                if (utils.isDeleteModalModule(change)) {
                  this[types.APPLY_DELETE_MODAL_MODULE]({ change, yDoc, transaction });
                }
              } else if (utils.isUpdatePage(change) || utils.isUpdatePageOptions(change)) {
                this[types.APPLY_UPDATE_PAGE]({ change, yDoc, transaction });
              } else if (utils.isAddPage(change)) {
                this[types.APPLY_ADD_PAGE]({ change, yDoc, transaction });
              } else if (utils.isDeletePage(change)) {
                this[types.APPLY_DELETE_PAGE]({ change, yDoc, transaction });
              } else if (utils.isAddModalCanvas(change)) {
                this[types.APPLY_ADD_MODAL_CANVAS]({ change, yDoc });
              } else if (utils.isDeleteModalCanvas(change)) {
                this[types.APPLY_DELETE_MODAL_CANVAS]({ change, yDoc, transaction });
              } else if (
                utils.isUpdateModalCanvas(change)
                || utils.isUpdateModalCanvasOptions(change)
              ) {
                this[types.APPLY_UPDATE_MODAL_CANVAS]({ change, yDoc, transaction });
              } else {
                changeNotHandled = true;
              }

              // If we've already published at least once, then flag unshared changes.
              if (sharedDoc.isHost
                  && !utils.isUpdateDraftMetadata(change)
                  && !utils.isUpdateDraftShares(change)
                  && !utils.isUpdateDraftOptionsChatEnabled(change)
                  && !yDoc.get('session').get('draftIsDirty')
                  && yDoc.get('draft').get('metadata').get('published_on')) {
                this[types.SET_DRAFT_IS_DIRTY](true);
              }
            });

            // if the change wasn't handled, update commit mutation and api update
            // for the entire draft
            if (changeNotHandled) {
              const updatedDraft = yDoc.toJSON().draft;
              this.draft = updatedDraft;
              if (sharedDoc.isHost) {
                this[types.ENQUEUE_DRAFT_PATCH](updatedDraft);
              }
            }

            // make sure there are no duplicate indexes as a result of changes
            this[types.CHECK_SORT_INDEXES]();

            /*
              Whenever a page or module is updated, check the z_positions for its
              modules to ensure there are no duplcates. If there are duplicates, then the
              duplicate values will be reassigned unique values. This can happen if two users
              move a module to the same layer at the same time. This ensures each module
              is always on its own layer.
            */
            this[types.CHECK_MODULE_Z_POSITIONS](changes);
          });

          appStore[types.UPDATE_LOADING](false);
          this.isConnected = true;
        });

        sharedDoc.on('host-changed', () => {
          /*
            When the host changes and the current user is the new host,
            save the current state of the draft to the server. This is a safety
            net in case the host user's session is closed before outstanding changes
            can be saved. With this, the newly set host will ensure any changes not saved
            by the previous host are saved to the server.
          */
          if (sharedDoc.isHost) {
            const yDoc = sharedDoc.document;
            if (yDoc) {
              this[types.ENQUEUE_DRAFT_PATCH](yDoc.toJSON().draft);
            }
          }
        });

        sharedDoc.on('connected-users-changed', (connectedUsers) => {
          const users = connectedUsers.map((user) => ({
            id: user.id,
            clientId: user.clientId,
            firstName: get(user, 'data.userData.firstName'),
            lastName: get(user, 'data.userData.lastName'),
            isHost: user.isHost,
            isCurrentUser: user.isCurrentUser,
            connectionTime: user.connectionTime,
          }));
          this.connectedUsers = users;
          this[types.SET_CONNECTED_USERS_COLORS]();
        });

        const onDisconnected = (loadingState) => {
          this.isConnected = false;
          this.connectedUsers = null;
          appStore[types.UPDATE_LOADING](loadingState);
          this[types.CLEAR_DRAFT]();
          this[types.CLOSE_UNDO_TRACKING]();
        };

        sharedDoc.on('disconnected', () => onDisconnected(true));
        sharedDoc.on('closed', () => onDisconnected(false));

        sharedDoc.on('error', async ({ error }) => {
          const closeSession = async () => {
            await this[types.CLOSE_EDITING_SESSION]();
            appStore[types.UPDATE_LOADING](false);
          };

          const setDefaultError = () => {
            errorStore[types.SET_ERROR]({
              active: true,
              error: {
                component: 'EditingSessionEndedError',
                options: {
                  error,
                },
              },
            });
          };

          const setPermissionError = async () => {
            // The user doesn't have sufficient permissions for this draft. To
            // prevent an infinite spinner, show the error page and stop trying
            // to load the draft
            let accessLevel;
            try {
              await this[types.GET_DRAFT_REFERENCES](payload);
            } catch (err) {
              errorStore[types.SET_ERROR]({
                active: true,
                error: get(err, 'response.data', err),
              });
            } finally {
              accessLevel = this.draftAccessLevel;
            }

            if (accessLevel === 'view' || accessLevel === 'readonly') {
              errorStore[types.SET_ERROR]({
                active: true,
                error: {
                  component: 'ViewPermissionsError',
                  options: {
                    draftReferenceId: draftPointerAssetReference
                      && draftPointerAssetReference.reference_id,
                    draftType: get(draftOptions, 'type'),
                  },
                },
              });
            } else {
              setDefaultError();
            }
          };

          const isPermissionError = error.status === 403 && error.type === 'PermissionError';

          /*
            Close the editing session for unrecoverable errors and then
            either show a permission error or the default error with error info.
          */
          if (error instanceof SharedDocumentPollingError
            || error instanceof SharedDocumentAuthError
            || error instanceof SharedDocumentCreateSessionError
            || error instanceof SharedDocumentLoadSessionError
            || error.message.includes('Network Error')
            || error.message.match(/timeout/i)
            || isPermissionError) {
            closeSession();
            if (isPermissionError) {
              setPermissionError();
            } else {
              setDefaultError();
            }
          }
        });

        sharedDoc.open();
      } catch (error) {
        await this[types.CLOSE_EDITING_SESSION]();
        appStore[types.UPDATE_LOADING](false);
        if (error instanceof DraftIsDeletedError) {
          throw error;
        } else {
          errorStore[types.SET_ERROR]({
            active: true,
            error: get(error, 'response.data', error),
          });
        }
      }
    },
    [types.SET_SHARED_DOCUMENT](sd) {
      // This action is only used in tests, to be able to replace
      // the sharedDoc easily
      sharedDoc = sd;
    },
    [types.SET_CONNECTED_USERS_COLORS]() {
      const { connectedUsers, connectedUsersColors } = this;

      // The draft owner should always get a color and be displayed
      const draftOwner = {
        clientId: get(this, 'draft.owner_guid'),
      };

      if (connectedUsers) {
        [draftOwner, ...connectedUsers].forEach((user) => {
          if (!user.clientId || user.clientId in connectedUsersColors) return;
          const colorIdx = Object.keys(connectedUsersColors).length % 15;
          connectedUsersColors[user.clientId] = USER_COLORS[colorIdx];
        });
        this.connectedUsersColors = connectedUsersColors;
      }
    },
    async [types.CLOSE_EDITING_SESSION]() {
      const chatStore = useChatStore();

      this[types.KILL_POLL]();
      chatStore[types.CLOSE_EDITING_SESSION](null);
      this[types.CLOSE_UNDO_TRACKING]();

      if (sharedDoc) {
        // Wait for the SharedDocument to clear its message queue before continuing.
        await sharedDoc.destroy();
        sharedDoc = null;
        this.isConnected = false;
        this.connectedUsers = null;
        this[types.CLEAR_DRAFT]();
        this.session = null;
      }
    },
    [types.INIT_DRAFT_UNDO]() {
      undoManager = new UndoManager();

      // Update flags in store that tell UI whether undo/redo is possible.
      const updateUndoRedoFlags = () => {
        this.canUndo = undoManager.canUndo;
        this.canRedo = undoManager.canRedo;
      };

      undoManager.on('stack-changed', updateUndoRedoFlags);

      updateUndoRedoFlags();
    },
    [types.REMOVE_FROM_UNDO_HISTORY](filter) {
      if (undoManager) {
        undoManager.clearStacks(filter);
      }
    },
    [types.CLOSE_UNDO_TRACKING]() {
      undoManager = null;
      this.canUndo = false;
      this.canRedo = false;
    },
    [types.VIEW_UNDO_REDO_ACTION](stackItem, actionType, action) {
      /*
        Try to ensure that the undo or redo change will be visible in the UI.
        Attempts to navigate to the page and open the panel where the change
        is taking place so the user can see it.
      */
      // Waits for the associated panel to open before executing the undo/redo command.
      let waitForPanel = false;
      let onAfterAction;
      // Get the panel name where the undo/redo changes can be seen by the user.
      const panel = stackItem.tags?.get('panel');
      const panelName = panel?.name;
      const modalStore = useModalStore();
      const clearModals = () => {
        if (modalStore.modals?.length) {
          modalStore[types.CLEAR_MODALS]();
          waitForPanel = true;
        }
      };
      const closeModalCanvasAndWait = () => {
        if (this.currentModalCanvasId) {
          this.currentModalCanvasId = null;
          waitForPanel = true;
        }
      };
      if (panelName === 'modalCanvasListModal') {
        closeModalCanvasAndWait();
        const type = 'ModalCanvasListModal';
        // If the modal is not open yet, open it.
        const modalIsOpen = last(modalStore.modals)?.type === type;
        const scrollToCanvasId = panel[`${actionType}FocusCanvasId`];
        const { selectCanvasId } = panel;
        /*
          If we're going to manually select the canvas in the modal,
          then don't set the selected canvas when opening the modal.
        */
        const selectCanvasOnOpen = !selectCanvasId;
        if (!modalIsOpen) {
          waitForPanel = true;
          modalStore[types.OPEN_MODAL]({
            type,
            moduleInfo: selectCanvasOnOpen
              ? panel.moduleInfo
              : omit(panel.moduleInfo, ['target']),
            scrollToSelected: false,
            focusSearch: false,
          });
        }
        /*
          Attempt to scroll the action into view within the canvas modal.
          When scrollToCanvasId is nil and selectCanvasId is true, then
          the current selected canvas in the modal will be unselected.
        */
        if (scrollToCanvasId || selectCanvasId) {
          onAfterAction = () => {
            nextTick(() => {
              bus.emit('modal-canvas-list:scroll-to', {
                canvasId: scrollToCanvasId,
                selectCanvasId,
              });
            });
          };
        }
      } else if (panelName === 'pageDrawer') {
        clearModals();
        // If a modal is open, then close it and wait.
        closeModalCanvasAndWait();
        // If the page drawer is not open, then open it and wait.
        if (!this.topNavDrawerIsOpen) {
          this.topNavDrawerIsOpen = true;
          waitForPanel = true;
        }
      } else {
        clearModals();
        // If the page drawer is open, then close it and wait.
        if (this.topNavDrawerIsOpen) {
          this.topNavDrawerIsOpen = false;
          waitForPanel = true;
        }
        // Get the page or modal this undo item is targeting.
        const pageOrModalId = stackItem.tags?.get('pageOrModalId');
        if (pageOrModalId) {
          if (panelName === 'modules') {
            // If a modal is open and needs to be closed or the page needs to change, then wait.
            if (this.currentModalCanvasId || this.currentPageId !== pageOrModalId) {
              waitForPanel = true;
            }
            // Navigate to the page where the change is taking place.
            this[types.VIEW_PAGE_BY_ID](pageOrModalId);
          } if (panelName === 'notes') {
            const notesIsOpen = this.editorDrawers.notes;
            /*
              If a modal needs to be closed or the page needs to change
              or the notes panel needs to be opened, then wait.
            */
            if (
              this.currentModalCanvasId
              || this.currentPageId !== pageOrModalId
              || !notesIsOpen
            ) {
              waitForPanel = true;
            }
            // If the notes panel is not open, then open it.
            if (!notesIsOpen) {
              this.editorDrawers.addModule = false;
              this.editorDrawers.customization = false;
              this.editorDrawers.notes = true;
            }
            this[types.VIEW_PAGE_BY_ID](pageOrModalId);
          } else if (panelName === 'modalModules') {
            /*
              If the modal needs to be opened or a different modal id
              needs to be loaded, then wait.
            */
            if (
              !this.currentModalCanvasId
              || this.currentModalCanvasId !== pageOrModalId
            ) {
              waitForPanel = true;
            }
            // Display the modal where the change is taking place.
            this[types.VIEW_MODAL_CANVAS_BY_ID](pageOrModalId);
          }
        }
      }
      if (waitForPanel) {
        // Turn off panel animations
        this.editorDrawersAreAnimated = false;
        // Disable undo/redo while opening the related panel.
        this.canUndo = false;
        this.canRedo = false;
        /*
          Wait a moment for the panel to be displayed before executing the undo or redo.
          This is so the change in the UI is not hidden and can be seen by the user.
        */
        setTimeout(() => {
          this.editorDrawersAreAnimated = true;
          // Run the undo/redo command
          action();
          if (onAfterAction) {
            onAfterAction();
          }
        }, 250);
      } else {
        // Run the undo/redo command
        action();
        if (onAfterAction) {
          onAfterAction();
        }
      }
    },
    [types.UNDO_DRAFT_EDIT]() {
      if (undoManager && undoManager.canUndo && this.canUndo) {
        const undoItem = first(undoManager.undoStack);
        // Try to ensure the next undo operation is visible in the UI.
        this[types.VIEW_UNDO_REDO_ACTION](undoItem, 'undo', () => {
          undoManager.undo();
        });
      }
    },
    [types.REDO_DRAFT_EDIT]() {
      if (undoManager && undoManager.canRedo && this.canRedo) {
        const redoItem = first(undoManager.redoStack);
        // Try to ensure the next redo operation is visible in the UI.
        this[types.VIEW_UNDO_REDO_ACTION](redoItem, 'redo', () => {
          undoManager.redo();
        });
      }
    },
    [types.COPY_MODAL_CANVASES]({
      modals,
      forceCopy = false,
      undoItem,
    }) {
      const copyItem = undoItem || new DocumentChangeStackItem(sharedDoc);
      const isStandaloneTransaction = !undoItem;

      const result = copyModals({
        modals,
        forceCopy,
      });
      const { modalCopyMap } = result;
      const modalCopies = result.modals.map(
        (modalCopy) => this[types.ADD_MODAL_CANVAS]({
          modalCanvas: modalCopy,
          undoItem: copyItem,
        }),
      );

      if (isStandaloneTransaction) {
        /*
          Store args needed to relaunch the modal canvas list in the undo item.
          Ensure the modal canvas list modal is opened when undoing / redoing
          this stack item.
        */
        const modalCanvasListModal = find(
          useModalStore()?.modals,
          (m) => m.type === 'ModalCanvasListModal',
        );
        const firstModalCopyId = first(modalCopies)?.id;
        const originalModalId = find(
          Object.keys(modalCopyMap),
          (key) => modalCopyMap[key]?.id === firstModalCopyId,
        );
        copyItem.tags.set('panel', {
          name: 'modalCanvasListModal',
          moduleInfo: modalCanvasListModal?.moduleInfo,
          /*
            If undoing the copy, then scroll to the original modal,
            otherwise scroll to the modal copy.
          */
          undoFocusCanvasId: originalModalId,
          redoFocusCanvasId: firstModalCopyId,
        });

        applyUndoable(copyItem);
      }

      return {
        modalCopies,
        modalCopyMap,
      };
    },
    [types.ADD_MODAL_CANVAS]({
      modalCanvas,
      undoItem,
    }) {
      const newModalCanvas = {
        ...utils.newModalCanvasTemplate(),
        ...modalCanvas,
      };

      if (!newModalCanvas.title) {
        newModalCanvas.title = getNextTitle(
          this.modalCanvases.map((c) => c.title),
          getWindowTitleFormat(),
        );
      }

      // Make sure we don't carry over old module ids
      newModalCanvas.modules = newModalCanvas.modules.map((mod) => ({
        ...mod,
        id: uuid(),
      }));

      const modals = utils.getModals(sharedDoc.document);
      if (modals) {
        undoItem.add(DocumentChange.add(utils.getModals, newModalCanvas));
      } else {
        const draftUpdates = { modals: [newModalCanvas] };
        undoItem.add(DocumentChange.update(utils.getDraft, draftUpdates));
      }

      return newModalCanvas;
    },
    [types.CREATE_MODAL_CANVAS]({ moduleInfo }) {
      // Create undo item for transaction.
      const undoItem = new DocumentChangeStackItem(sharedDoc);

      // Add the new modal canvas to the transaction.
      const modalCanvas = this[types.ADD_MODAL_CANVAS]({ undoItem });

      // Link the specified module to the modal canvas.
      const linkResult = this[types.LINK_MODAL_CANVAS]({
        undoItem,
        modalCanvas,
        moduleInfo,
      });

      // Get the previous modal canvas this module was linked to.
      const { previousModalCanvasId } = linkResult || {};

      // Launch the modal canvas modal when this undo item is triggered.
      undoItem.tags.set('panel', {
        name: 'modalCanvasListModal',
        moduleInfo,
        undoFocusCanvasId: previousModalCanvasId,
        redoFocusCanvasId: modalCanvas.id,
        selectCanvasId: true,
      });

      // Apply and add to undo manager.
      applyUndoable(undoItem);

      return modalCanvas;
    },
    [types.LINK_MODAL_CANVAS]({
      modalCanvas,
      moduleInfo,
      undoItem,
    }) {
      const linkItem = undoItem || new DocumentChangeStackItem(sharedDoc);
      const isStandaloneTransaction = !undoItem;
      const newModalCanvasId = modalCanvas.id;
      let previousModalCanvasId;

      const mod = cloneDeep(this.getModuleById(moduleInfo.moduleId));
      if (mod) {
        const update = () => {
          this[types.UPDATE_MODULES]({
            canvasName: moduleInfo.canvasName,
            modules: [mod],
            undoItem: linkItem,
          });

          /*
            If this action is not part of a larger transaction, then add the panel tag
            to launch the modal canvas modal when this undo item is triggered.
          */
          if (isStandaloneTransaction) {
            linkItem.tags.set('panel', {
              name: 'modalCanvasListModal',
              moduleInfo,
              undoFocusCanvasId: previousModalCanvasId,
              redoFocusCanvasId: newModalCanvasId,
              selectCanvasId: true,
            });
          }
        };
        if (mod.type === 'text') {
          const doc = docFromString(mod.options.content);
          if (doc) {
            const buttonAttrs = findDocNode(doc, (node) => node['data-id'] === moduleInfo.id);
            if (buttonAttrs) {
              previousModalCanvasId = buttonAttrs['data-target-page'];
              buttonAttrs['data-action'] = 'window';
              buttonAttrs['data-target-page'] = modalCanvas.id;
              // Make this button active again if it is currently inactive.
              if (buttonAttrs['data-inactive'] === 'true') {
                buttonAttrs['data-inactive'] = 'false';
              }
              mod.options.content = toDocString(doc);
              update();
              nextTick(() => {
                // Force the text editor to update its content from the app store.
                bus.emit('text-editor:update-content', { moduleId: moduleInfo.moduleId });
              });
            }
          }
        } else if (mod.options.action !== 'window' || mod.options.target_page !== modalCanvas.id) {
          previousModalCanvasId = mod.options.target_page;
          mod.options.target_page = modalCanvas.id;
          mod.options.action = 'window';
          update();
        }

        if (isStandaloneTransaction) {
          // If this is an isolated transaction, then apply it.
          applyUndoable(linkItem);
        }

        return {
          newModalCanvasId,
          previousModalCanvasId,
        };
      }

      return null;
    },
    [types.UNLINK_MODAL_CANVAS]({ modalCanvas, undoItem }) {
      const updatedModules = [];
      const updatedNotes = [];
      const modulesWithLinksRemoved = (modules) => (
        chain(modules)
          .map((m) => removeWindowLinks({
            module: cloneDeep(m),
            modalCanvasId: modalCanvas.id,
            disableTextLinks: true,
          }))
          .filter((m) => m.hasWindowLinks)
          .map((m) => m.module)
          .value()
      );
      forEach(this.draft.pages, (page) => {
        updatedModules.push(...modulesWithLinksRemoved(page.modules));
        updatedNotes.push(...modulesWithLinksRemoved(page.notes));
      });
      this[types.UPDATE_MODULES]({
        modules: updatedModules,
        canvasName: 'modules',
        undoItem,
      });
      this[types.UPDATE_MODULES]({
        modules: updatedNotes,
        canvasName: 'notes',
        undoItem,
      });
    },
    [types.DELETE_MODAL_CANVAS](modalCanvas) {
      const deleteItem = new DocumentChangeStackItem(sharedDoc);
      deleteItem.add(DocumentChange.delete(utils.getModals, modalCanvas));

      // Remove links to this modal canvas from modules in the draft.
      this[types.UNLINK_MODAL_CANVAS]({
        modalCanvas,
        undoItem: deleteItem,
      });

      /*
        Store args needed to relaunch the modal canvas list in the undo item.
        Ensure the modal canvas list modal is opened when undoing / redoing
        this stack item.
      */
      const modalCanvasListModal = find(
        useModalStore()?.modals,
        (m) => m.type === 'ModalCanvasListModal',
      );
      deleteItem.tags.set('panel', {
        name: 'modalCanvasListModal',
        moduleInfo: modalCanvasListModal?.moduleInfo,
        undoFocusCanvasId: modalCanvas.id,
      });

      // Apply changes and add to undo manager.
      applyUndoable(deleteItem);

      return modalCanvas;
    },
    [types.UPDATE_MODAL_CANVAS](modalCanvasUpdates) {
      // Create undo item.
      const updateModalItem = new DocumentChangeStackItem(sharedDoc);

      // Opens the modal canvas to view the undo/redo changes.
      updateModalItem.tags.set('panel', { name: 'modalModules' });
      updateModalItem.tags.set('pageOrModalId', modalCanvasUpdates.id);

      // Add update document change
      updateModalItem.add(DocumentChange.update(
        utils.findYModalById,
        modalCanvasUpdates,
      ));

      // Clear the copy source_id from the modal
      this[types.CLEAR_MODAL_CANVAS_SOURCE_ID]({
        undoItem: updateModalItem,
      });

      // Apply changes and add to undo manager.
      applyUndoable(updateModalItem);
    },
    [types.UPDATE_MODAL_CANVAS_OPTIONS]({
      modalCanvasId,
      optionsUpdates,
      undoItem,
    }) {
      // Create undo item.
      const updateOptionsItem = undoItem || new DocumentChangeStackItem(sharedDoc);
      const isStandaloneTransaction = !undoItem;

      if (isStandaloneTransaction) {
        // Opens the modal canvas to view the undo/redo changes.
        updateOptionsItem.tags.set('panel', { name: 'modalModules' });
        updateOptionsItem.tags.set('pageOrModalId', modalCanvasId);
      }

      // Add update document change
      updateOptionsItem.add(DocumentChange.update(
        utils.findYModalOptionsById,
        optionsUpdates,
        modalCanvasId,
      ));

      // Clear the copy source_id from the modal
      this[types.CLEAR_MODAL_CANVAS_SOURCE_ID]({
        undoItem: updateOptionsItem,
      });

      if (isStandaloneTransaction) {
        // Apply changes and add to undo manager.
        applyUndoable(updateOptionsItem);
      }
    },
    [types.RESIZE_MODAL_CANVAS]({
      modalCanvasId,
      width = MODAL_CANVAS_WIDTH,
      height = MODAL_CANVAS_HEIGHT,
      fitModulesY = true,
      fitModulesX = true,
      undoItem,
    }) {
      // Get a copy of the modal canvas
      const modalCanvas = cloneDeep(this.modalCanvases.find((m) => m.id === modalCanvasId));
      if (!modalCanvas) return;

      // Create undo item.
      const resizeItem = undoItem || new DocumentChangeStackItem(sharedDoc);
      const isStandaloneTransaction = !undoItem;

      if (isStandaloneTransaction) {
        // Opens the modal canvas to view the undo/redo changes.
        resizeItem.tags.set('panel', { name: 'modalModules' });
        resizeItem.tags.set('pageOrModalId', modalCanvasId);
      }
      /*
        When adjusting a module's position, use this padding from the
        edge of the canvas.
      */
      const modulePadding = nebulaSpace1x;
      /*
        Update module positions to ensure they are within the bounds of
        the new canvas size if possible.
      */
      modalCanvas.modules.forEach((mod) => {
        // Get the dimensions for the module's box.
        const { layout } = mod;
        let { x_position: x, y_position: y } = layout;
        const right = x + layout.width;
        const bottom = y + layout.height;

        // Adjust the module's position to be inside of the new canvas size.
        if (fitModulesX) {
          if (right > width) x -= (right - width + modulePadding);
          if (x < 0) x = modulePadding;
        }
        if (fitModulesY) {
          if (bottom > height) y -= (bottom - height + modulePadding);
          if (y < 0) y = modulePadding;
        }

        // If we made adjustments, update the module.
        if (x !== layout.x_position || y !== layout.y_position) {
          const modCopy = cloneDeep(mod);
          modCopy.layout.x_position = x;
          modCopy.layout.y_position = y;
          this[types.UPDATE_MODULE]({
            canvasName: 'modalModules',
            payload: modCopy,
            undoItem: resizeItem,
          });
        }
      });

      // Resize the modal canvas
      this[types.UPDATE_MODAL_CANVAS_OPTIONS]({
        modalCanvasId,
        optionsUpdates: { width, height },
        undoItem: resizeItem,
      });

      // Apply changes and add to undo stack.
      if (isStandaloneTransaction) {
        applyUndoable(resizeItem);
      }
    },
    [types.CLEAR_MODAL_CANVAS_SOURCE_ID]({ undoItem }) {
      /*
        Clear source_id from a copied modal whenever a change is made.
        This tells us that the copied modal has changed since it was pasted
        and if the original modal is pasted again, we should bring in a new copy.
      */
      undoItem.add(DocumentChange.update(
        utils.findYModalOptionsById,
        { id: this.currentModalCanvasId, source_id: undefined },
      ));
    },
    async [types.APPLY_ADD_MODAL_CANVAS]({ change, yDoc }) {
      // Get the modal
      chain([...change.changes.added.values()])
        .map((added) => added.content.type.get('id'))
        .uniq()
        .value()
        .forEach((newModalId) => {
          const modals = yDoc.get('draft').get('modals');
          const modal = yjsHelper.find(modals, (yModal) => yModal.get('id') === newModalId);
          if (modal) {
            const newModal = modal.toJSON();

            // Update state
            this.draft.modals.push(newModal);

            if (sharedDoc.isHost) {
              this[types.ENQUEUE_MODAL_PATCH]({ modal: newModal, action: 'add' });
            }
          }
        });
    },
    [types.APPLY_DELETE_MODAL_CANVAS]({ change, transaction }) {
      // Get modal canvases that were deleted and remove them from the store.
      chain([...change.changes.deleted.values()])
        .map((deleted) => deleted.content.type._map.get('id').content.arr[0])
        .uniq()
        .value()
        .forEach((modalId) => {
          // Remove the modal from the app store if it exists
          const idx = this.draft.modals?.findIndex((m) => m.id === modalId);
          if (idx === -1) return;

          // Close modal canvas if it is currently open.
          if (this.currentModalCanvasId === modalId) {
            this.currentModalCanvasId = null;
          }

          this.draft.modals.splice(idx, 1);

          // If this is the host user, then update the server with changes.
          if (sharedDoc.isHost) {
            this[types.ENQUEUE_MODAL_PATCH]({ modal: { id: modalId }, action: 'remove' });
          }

          /*
            If the modal was deleted by another user, then remove any undo items related
            to this modal from this user's undo history.
          */
          if (!transactionIsLocal(transaction)) {
            // Remove history for updates to this modal.
            this[types.REMOVE_FROM_UNDO_HISTORY]((item) => (
              item.tags.get(modalId) || item.tags.get('pageOrModalId') === modalId
            ));
          }
        });
    },
    [types.APPLY_UPDATE_MODAL_CANVAS]({ change, transaction }) {
      const modal = utils.isUpdateModalCanvasOptions(change)
        ? change.target.parent.toJSON()
        : change.target.toJSON();

      this[types.COMMIT_UPDATE_MODAL](modal);

      if (sharedDoc.isHost) {
        this[types.ENQUEUE_MODAL_PATCH]({ modal, action: 'update' });
      }

      /*
        If the modal was updated by another user, then:
        1. Remove any updates to the same properties from this user's
        undo history for this modal. This is to prevent this user from
        undoing another user's more recent change to a property value.
        2. Remove any add or delete undo items for this modal from
        this user's undo history.
      */
      if (!transactionIsLocal(transaction)) {
        const updatedKeys = [...change.changes?.keys?.entries()];
        this[types.REMOVE_FROM_UNDO_HISTORY]((item) => {
          const tags = item.tags.get(modal.id);
          if (!tags) return false;
          return tags.has('add')
            || tags.has('delete')
            || updatedKeys.some((key) => tags.has('update') && tags.has(first(key)));
        });
      }
    },
    [types.SET_DURATION](duration) {
      this.videoDurations[duration.id] = duration;
    },
    async [types.CREATE_PREVIEW](previewData) {
      const assetStore = useAssetStore();
      const assessmentStore = useAssessmentStore();
      const errorStore = useErrorStore();
      try {
        if (!this.templates) await this[types.GET_TEMPLATES]();

        const draft = {
          id: uuid(),
          name: previewData.name || utils.defaultDraftName(previewData.options.type),
          options: previewData.options,
          modals: [],
          pages: previewData.pages.map((page, pageIndex) => {
            const pageTemplate = find(
              this.templates,
              (template) => template.name === page.template,
            );
            if (!pageTemplate) {
              throw new Error(i18next.t('Template %(template)s was not found.', { template: i18next.t(page.template) }));
            }
            const previewPage = merge(
              utils.newPageTemplate(previewData.options.type),
              {
                options: page.options,
                title: page.title || undefined,
                sort_index: pageIndex + 1,
              },
            );
            page.modules.forEach((mod) => {
              const templateMod = first(pageTemplate.pages).modules.find((tempMod) => {
                const modDisplayId = get(mod, 'options.display_id');
                const tempModDisplayId = get(tempMod, 'options.display_id');
                return tempModDisplayId && modDisplayId && tempModDisplayId === modDisplayId;
              });
              if (templateMod) {
                // All draft types use pixels now
                const layout = layoutToPixels(cloneDeep(templateMod.layout));

                if (templateMod.type === 'placeholder') {
                  const defaultMod = moduleDefaultsList.find((func) => func().type === mod.type);
                  if (defaultMod) {
                    const gridDisabled = previewData.options.type !== 'board';
                    const previewMod = merge(
                      utils.newModuleTemplate(defaultMod(gridDisabled)),
                      {
                        layout,
                        options: mod.options,
                      },
                    );
                    previewMod.options = omit(previewMod.options, 'display_id');
                    previewPage.modules.push(previewMod);
                  }
                } else {
                  const previewMod = merge(
                    utils.newModuleTemplate(cloneDeep(templateMod)),
                    {
                      layout,
                      options: {
                        ...mod.options,
                      },
                    },
                  );
                  previewMod.options = omit(previewMod.options, 'display_id');

                  // Special handling for text modules.
                  if (templateMod.type === 'text') {
                    /*
                      Need to use the text placeholder textile string (from the text module
                      within the template) to format the content provided in the preview data.
                    */
                    previewMod.options.data_type = 'textile';
                    const useUnformattedContent = () => {
                      // No text formatting available in the template so use plain paragraph tag.
                      previewMod.options.content = `p. ${previewMod.options.content}`;
                    };
                    const placeholder = get(previewMod, 'options.text_placeholder');
                    if (placeholder) {
                      const placeholderTextSlotExp = /\$t{(.*)}/;
                      const placeholderTextSlot = placeholder
                        && placeholder.match(placeholderTextSlotExp);

                      if (placeholderTextSlot) {
                        previewMod.options.content = placeholder.replace(
                          placeholderTextSlotExp,
                          previewMod.options.content,
                        );
                      } else {
                        useUnformattedContent();
                      }
                    } else {
                      useUnformattedContent();
                    }
                  }

                  previewPage.modules.push(previewMod);
                }
              }
            });

            return previewPage;
          }),
        };

        this[types.CLOSE_EDITOR_DRAWERS]();
        this.draft = draft;
        this.accessLevelForDraft = null;
        assetStore[types.CACHE_ASSETS_AND_UPLOADS]();
        assessmentStore[types.CACHE_TEIS]();
        await this[types.VIEW_PAGE_BY_ID](first(draft.pages).id);
        this[types.SET_IS_PREVIEW_MODE](true);
        bus.emit('preview-loaded', omit(cloneDeep(draft), 'id'));
      } catch (error) {
        this[types.CLEAR_DRAFT]();
        errorStore[types.SET_ERROR]({
          active: true,
          error: get(error, 'response.data') || {
            meta: {
              message: error.message,
            },
          },
        });
        bus.emit('preview-error', error);
      }
    },
    async [types.SAVE_DRAFT](payload) {
      const appStore = useAppStore();
      const { draft, originAssetId } = payload;
      const postUrl = originAssetId ? `/drafts/?origin_asset_id=${originAssetId}` : '/drafts/';

      const result = await this.titanApiFetch(postUrl, {
        method: 'POST',
        body: draft,
        headers: {
          'X-Token': appStore.apiToken,
        },
      });
      if (result.status !== 200) {
        throw result;
      }
      return result.data?.draft;
    },
    async [types.CREATE_CREATION](draftId) {
      const appStore = useAppStore();
      // Note: This draftId must be the mongo id, not the asset id.
      const result = await this.titanApiFetch('/creations/', {
        method: 'POST',
        body: { id: draftId },
        headers: {
          'X-Token': appStore.apiToken,
        },
      });

      if (result.status !== 201) throw result;
      return result.data?.creation;
    },
  },
});

export default useEditorStore;
